import { todayMx, shiftDateStr, todayMxDate } from '../lib/dateUtils';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import { toast } from 'sonner';
import { format, parseISO, addDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../components/ui/dialog';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Plus, 
  Search, 
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  Copy,
  Calendar,
  Eye,
  Loader2,
  X,
  Clock,
  User,
  Phone,
  MapPin,
  DollarSign,
  FileText,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Receipt,
  Send,
  CalendarClock,
  Banknote,
  UserPlus,
  History,
  AlertCircle,
  Users,
  Package
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { InlineText, InlineTime, InlineSelect, InlineDate } from '../components/InlineEdit';
import ScheduleRecommendation from '../components/ScheduleRecommendation';

const statusLabels = {
  pendiente: 'Pendiente',
  confirmado: 'Confirmado',
  en_proceso: 'En Proceso',
  visitado: 'Visitado',
  finalizado: 'Finalizado',
  cancelado: 'Cancelado',
  reagendado: 'Reagendado',
  reasignado: 'Reasignado',
  presupuesto_pendiente: 'Presup. Pendiente',
  presupuesto_aprobado: 'Presup. Aprobado',
  c_e: 'C/E'
};

// Prioridad de ordenamiento: activos arriba, finalizados después, cancelados al fondo
const STATUS_SORT_PRIORITY = {
  pendiente: 0, confirmado: 0, en_proceso: 0, visitado: 0,
  reagendado: 0, reasignado: 0, presupuesto_pendiente: 0, presupuesto_aprobado: 0, c_e: 0,
  finalizado: 1, cancelado: 2
};

const statusOptions = Object.entries(statusLabels).map(([value, label]) => ({ value, label }));

const serviceTypes = [
  'Sondeo de drenaje',
  'Plomería',
  'Electricidad',
  'Aire Acondicionado',
  'Detección de fugas',
  'Mantenimiento general',
  'Venta de minisplit',
  'Otro'
];

const contactMediums = [
  'Teléfono',
  'WhatsApp',
  'Correo',
  'Referido',
  'Redes sociales',
  'Otro'
];

// Colores asignados a cada técnico para identificación visual rápida
const TECHNICIAN_COLORS = {
  'German Ortiz': { bg: 'bg-blue-100 dark:bg-blue-900/40', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-500', dot: 'bg-blue-500' },
  'Jesús Arana': { bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-700 dark:text-green-300', border: 'border-green-500', dot: 'bg-green-500' },
  'Erik Burgos': { bg: 'bg-orange-100 dark:bg-orange-900/40', text: 'text-orange-700 dark:text-orange-300', border: 'border-orange-500', dot: 'bg-orange-500' },
  'Jose Gutierrez': { bg: 'bg-purple-100 dark:bg-purple-900/40', text: 'text-purple-700 dark:text-purple-300', border: 'border-purple-500', dot: 'bg-purple-500' },
  'Alberto Martinez': { bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-700 dark:text-red-300', border: 'border-red-500', dot: 'bg-red-500' },
  'default': { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-700 dark:text-gray-300', border: 'border-gray-500', dot: 'bg-gray-500' }
};

const getTechnicianColor = (name) => {
  return TECHNICIAN_COLORS[name] || TECHNICIAN_COLORS['default'];
};

export default function Services() {
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const todayStr = todayMx();
  const [filters, setFilters] = useState({
    status: 'all',
    technician_id: 'all',
    assigned_by_id: 'all',
    date_from: todayStr,
    date_to: todayStr
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedTechnicianTab, setSelectedTechnicianTab] = useState('all');
  const [groupByTechnician, setGroupByTechnician] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [formData, setFormData] = useState(getEmptyForm());
  const [saving, setSaving] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);
  const [clientHistoryOpen, setClientHistoryOpen] = useState(false);
  const [clientHistory, setClientHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Finalize service dialog state
  const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false);
  const [serviceToFinalize, setServiceToFinalize] = useState(null);
  const [finalizeData, setFinalizeData] = useState({
    folio_nota: '',
    monto_cobrado: 0,
    comentario_cierre: '',
    enviar_a_presupuesto: false
  });
  const [finalizing, setFinalizing] = useState(false);

  // Reschedule dialog state
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [serviceToReschedule, setServiceToReschedule] = useState(null);
  const [rescheduleData, setRescheduleData] = useState({
    nueva_fecha: '',
    hora_inicio: '09:00',
    hora_fin: '10:00',
    nuevo_tecnico_id: '',
    nuevo_tecnico_nombre: '',
    costo_total_cotizado: 0,
    anticipo: 0,
    motivo: '',
    // Estado del evento del día actual
    estatus_evento_actual: 'reagendado',
    acudio_domicilio: false,
    observaciones_dia_actual: ''
  });
  const [rescheduling, setRescheduling] = useState(false);

  // Reassign service dialog state
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [serviceToReassign, setServiceToReassign] = useState(null);
  const [reassignData, setReassignData] = useState({
    anticipo_cobrado: 0,
    comentario_visita: '',
    motivo_reasignacion: '',
    nuevo_tecnico_id: '',
    nuevo_tecnico_nombre: '',
    nueva_fecha: '',
    nueva_hora_inicio: '09:00',
    nueva_hora_fin: '10:00',
    costo_total_servicio: 0
  });
  const [reassigning, setReassigning] = useState(false);

  // Service detail/history dialog
  const [serviceDetailOpen, setServiceDetailOpen] = useState(false);
  const [selectedServiceDetail, setSelectedServiceDetail] = useState(null);

  // Equipment for minisplit sales
  const [availableEquipment, setAvailableEquipment] = useState([]);
  const [equiposVenta, setEquiposVenta] = useState([]);

  // Technician availability slots
  const [techSchedule, setTechSchedule] = useState([]);
  const [loadingSchedule, setLoadingSchedule] = useState(false);

  function getEmptyForm() {
    const today = todayMx();
    return {
      service_date: today,
      start_time: '09:00',
      end_time: '10:00',
      client_name: '',
      client_id: '',
      phones: [''],
      address: '',
      address_reference: '',
      service_type: '',
      description: '',
      technician_id: 'unassigned',
      technician_name: '',
      quoted_cost: 0,
      internal_comments: '',
      budget_info: '',
      process_info: '',
      status: 'pendiente',
      contact_medium: ''
    };
  }

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.technician_id && filters.technician_id !== 'all') params.append('technician_id', filters.technician_id);
      if (filters.assigned_by_id && filters.assigned_by_id !== 'all') params.append('assigned_by_id', filters.assigned_by_id);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);

      const [servicesRes, techsRes, usersRes, clientsRes] = await Promise.all([
        axios.get(`${API}/services?${params.toString()}`),
        axios.get(`${API}/technicians`),
        axios.get(`${API}/users`).catch(() => ({ data: [] })),
        axios.get(`${API}/clients`)
      ]);

      setServices(servicesRes.data);
      setTechnicians(techsRes.data);
      setUsers(usersRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [search, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch technician schedule when tech + date selected
  useEffect(() => {
    if (selectedTechnicianTab === 'all' || !filters.date_from || filters.date_from !== filters.date_to) {
      setTechSchedule([]);
      return;
    }
    // Don't show for past dates (Ayer)
    const today = todayMx();
    if (filters.date_from < today) {
      setTechSchedule([]);
      return;
    }
    const tech = technicians.find(t => t.name === selectedTechnicianTab);
    if (!tech) { setTechSchedule([]); return; }

    const fetchSchedule = async () => {
      setLoadingSchedule(true);
      try {
        const res = await axios.get(`${API}/services/technician-schedule/${tech.id}?date=${filters.date_from}`);
        setTechSchedule(res.data);
      } catch { setTechSchedule([]); }
      finally { setLoadingSchedule(false); }
    };
    fetchSchedule();
  }, [selectedTechnicianTab, filters.date_from, filters.date_to, technicians]);

  // Función para procesar servicios y agregar eventos históricos cuando hay filtro de fecha
  const getServicesWithHistoricalEvents = useCallback(() => {
    // Si no hay filtro de fecha, retornar servicios normales (NUNCA marcados como históricos)
    if (!filters.date_from && !filters.date_to) {
      // Asegurar que ningún servicio tenga la marca de histórico
      return services.map(s => ({
        ...s,
        _es_evento_historico: false,
        _display_id: s.id
      }));
    }

    const result = [];
    const dateFrom = filters.date_from;
    const dateTo = filters.date_to || filters.date_from;

    services.forEach(service => {
      // SIEMPRE incluir el servicio original/activo si su fecha cae en el rango
      // Este es el servicio PRINCIPAL y EDITABLE
      if (service.service_date >= dateFrom && service.service_date <= dateTo) {
        result.push({
          ...service,
          _es_evento_historico: false, // EXPLÍCITAMENTE marcar como NO histórico
          _display_id: service.id // ID original para el servicio activo
        });
      }

      // ADEMÁS, buscar eventos históricos dentro del rango de fechas
      // Estos son registros de SOLO LECTURA de visitas anteriores
      if (service.eventos && service.eventos.length > 0) {
        service.eventos.forEach(evento => {
          // Solo agregar como histórico si:
          // 1. El evento está dentro del rango del filtro
          // 2. El evento NO es pendiente (ya fue procesado)
          // 3. La fecha del evento es DIFERENTE a la fecha actual del servicio
          if (evento.fecha_programada >= dateFrom && 
              evento.fecha_programada <= dateTo &&
              evento.estatus_evento !== 'pendiente' &&
              evento.fecha_programada !== service.service_date) {
            result.push({
              ...service,
              // Marcar EXPLÍCITAMENTE como evento histórico (solo lectura)
              _es_evento_historico: true,
              _evento_fecha: evento.fecha_programada,
              _evento_estatus: 'visitado',
              _evento_acudio: evento.acudio_domicilio,
              _evento_tecnico: evento.tecnico_nombre,
              _evento_tecnico_id: evento.tecnico_id,
              _evento_observaciones: evento.observaciones,
              _evento_anticipo: evento.anticipo_evento,
              _evento_movido_a: evento.nueva_fecha_programada,
              // Datos de visualización del evento histórico
              service_date: evento.fecha_programada,
              start_time: evento.hora_inicio,
              end_time: evento.hora_fin,
              technician_name: evento.tecnico_nombre,
              technician_id: evento.tecnico_id,
              // ID único para evitar duplicados en React key
              _display_id: `${service.id}-evento-${evento.id}`
            });
          }
        });
      }

      // Compatibilidad con historial_asignaciones (formato antiguo)
      if (!service.eventos?.length && service.historial_asignaciones && service.historial_asignaciones.length > 0) {
        service.historial_asignaciones.forEach((asig, idx) => {
          if (asig.fecha_anterior >= dateFrom && 
              asig.fecha_anterior <= dateTo &&
              asig.fecha_anterior !== service.service_date) {
            result.push({
              ...service,
              _es_evento_historico: true, // SOLO LECTURA
              _evento_fecha: asig.fecha_anterior,
              _evento_estatus: 'visitado',
              _evento_acudio: asig.acudio_domicilio,
              _evento_tecnico: asig.tecnico_anterior_nombre,
              _evento_tecnico_id: asig.tecnico_anterior_id,
              _evento_observaciones: asig.observaciones_dia || asig.comentarios_visita_anterior,
              _evento_anticipo: asig.anticipo_registrado,
              _evento_movido_a: asig.fecha_servicio,
              service_date: asig.fecha_anterior,
              start_time: asig.horario_anterior?.split(' - ')[0] || '',
              end_time: asig.horario_anterior?.split(' - ')[1] || '',
              technician_name: asig.tecnico_anterior_nombre,
              technician_id: asig.tecnico_anterior_id,
              _display_id: `${service.id}-asig-${idx}`
            });
          }
        });
      }
    });

    // Ordenar por fecha y hora
    return result.sort((a, b) => {
      const dateCompare = (a.service_date || '').localeCompare(b.service_date || '');
      if (dateCompare !== 0) return dateCompare;
      return (a.start_time || a.service_time || '').localeCompare(b.start_time || b.service_time || '');
    });
  }, [services, filters.date_from, filters.date_to]);

  // Obtener servicios procesados y ordenados (activos arriba, finalizados después, cancelados al fondo)
  const displayServices = (() => {
    const raw = getServicesWithHistoricalEvents();
    return raw.sort((a, b) => {
      // 1. Prioridad por estado: activos (0) > finalizados (1) > cancelados (2)
      const priorityA = STATUS_SORT_PRIORITY[a.status] ?? 0;
      const priorityB = STATUS_SORT_PRIORITY[b.status] ?? 0;
      if (priorityA !== priorityB) return priorityA - priorityB;
      // 2. Dentro de la misma prioridad: por fecha descendente (más reciente primero)
      const dateCompare = (b.service_date || '').localeCompare(a.service_date || '');
      if (dateCompare !== 0) return dateCompare;
      // 3. Misma fecha: por horario ascendente (más temprano primero)
      return (a.start_time || '').localeCompare(b.start_time || '');
    });
  })();

  const handleQuickFilter = (filterType) => {
    const today = todayMxDate();
    let newFilters = { ...filters, status: 'all', date_from: '', date_to: '' };

    switch (filterType) {
      case 'yesterday':
        const yesterday = addDays(today, -1);
        newFilters.date_from = format(yesterday, 'yyyy-MM-dd');
        newFilters.date_to = format(yesterday, 'yyyy-MM-dd');
        break;
      case 'today':
        newFilters.date_from = format(today, 'yyyy-MM-dd');
        newFilters.date_to = format(today, 'yyyy-MM-dd');
        break;
      case 'tomorrow':
        const tomorrow = addDays(today, 1);
        newFilters.date_from = format(tomorrow, 'yyyy-MM-dd');
        newFilters.date_to = format(tomorrow, 'yyyy-MM-dd');
        break;
      case 'week':
        newFilters.date_from = format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd');
        newFilters.date_to = format(endOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd');
        break;
      case 'month':
        newFilters.date_from = format(startOfMonth(today), 'yyyy-MM-dd');
        newFilters.date_to = format(endOfMonth(today), 'yyyy-MM-dd');
        break;
      case 'all_dates':
        // Mostrar todos sin filtro de fecha
        break;
      default:
        if (statusLabels[filterType]) {
          newFilters.status = filterType;
        }
    }

    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters({
      status: 'all',
      technician_id: 'all',
      assigned_by_id: 'all',
      date_from: '',
      date_to: ''
    });
    setSearch('');
  };

  const openNewDialog = () => {
    setSelectedService(null);
    setFormData({
      ...getEmptyForm(),
      assigned_by: user.full_name,
      assigned_by_id: user.id
    });
    setEquiposVenta([]);
    // Fetch available equipment
    axios.get(`${API}/inventory/available`).then(r => setAvailableEquipment(r.data)).catch(() => {});
    setDialogOpen(true);
  };

  const openEditDialog = (service) => {
    setSelectedService(service);
    setFormData({
      ...service,
      phones: service.phones?.length ? service.phones : [''],
      technician_id: service.technician_id || 'unassigned',
      start_time: service.start_time || service.service_time || '09:00',
      end_time: service.end_time || '10:00'
    });
    setEquiposVenta(service.equipos_venta || []);
    axios.get(`${API}/inventory/available`).then(r => setAvailableEquipment(r.data)).catch(() => {});
    setDialogOpen(true);
  };

  const openDetailDialog = async (service) => {
    try {
      const res = await axios.get(`${API}/services/${service.id}`);
      setSelectedService(res.data);
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Error al cargar detalles');
    }
  };

  const handleClientSelect = (client) => {
    setFormData({
      ...formData,
      client_id: client.id,
      client_name: client.full_name,
      phones: client.phones?.length ? client.phones : [''],
      address: client.addresses?.[0]?.street || '',
      address_reference: client.addresses?.[0]?.reference || ''
    });
    setClientSearch('');
    setShowClientSuggestions(false);
  };

  const handleViewClientHistory = async (client, e) => {
    e.stopPropagation();
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/clients/${client.id}`);
      setClientHistory(res.data);
      setClientHistoryOpen(true);
    } catch (error) {
      toast.error('Error al cargar historial del cliente');
    } finally {
      setLoadingHistory(false);
    }
  };

  const selectClientFromHistory = () => {
    if (clientHistory) {
      setFormData({
        ...formData,
        client_id: clientHistory.id,
        client_name: clientHistory.full_name,
        phones: clientHistory.phones?.length ? clientHistory.phones : [''],
        address: clientHistory.addresses?.[0]?.street || '',
        address_reference: clientHistory.addresses?.[0]?.reference || ''
      });
      setClientSearch('');
      setShowClientSuggestions(false);
      setClientHistoryOpen(false);
      toast.success('Cliente seleccionado');
    }
  };

  const handleTechnicianChange = (techId) => {
    if (techId === 'unassigned') {
      setFormData({
        ...formData,
        technician_id: 'unassigned',
        technician_name: ''
      });
    } else {
      const tech = technicians.find(t => t.id === techId);
      setFormData({
        ...formData,
        technician_id: techId,
        technician_name: tech?.name || ''
      });
    }
  };

  const handlePhoneChange = (index, value) => {
    const newPhones = [...formData.phones];
    newPhones[index] = value;
    setFormData({ ...formData, phones: newPhones });
  };

  const addPhone = () => {
    setFormData({ ...formData, phones: [...formData.phones, ''] });
  };

  const removePhone = (index) => {
    const newPhones = formData.phones.filter((_, i) => i !== index);
    setFormData({ ...formData, phones: newPhones.length ? newPhones : [''] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_name || !formData.service_date || !formData.service_type) {
      toast.error('Por favor completa los campos requeridos');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        phones: formData.phones.filter(p => p.trim()),
        quoted_cost: parseFloat(formData.quoted_cost) || 0,
        technician_id: formData.technician_id === 'unassigned' ? '' : formData.technician_id,
        equipos_venta: equiposVenta.length > 0 ? equiposVenta : undefined
      };

      let serviceId;
      if (selectedService) {
        await axios.put(`${API}/services/${selectedService.id}`, data);
        serviceId = selectedService.id;
        toast.success('Servicio actualizado');
      } else {
        data.assigned_by = user.full_name;
        data.assigned_by_id = user.id;
        const res = await axios.post(`${API}/services`, data);
        serviceId = res.data.id;
        toast.success('Servicio creado');
      }

      // Assign equipment to service if minisplit sale
      if (formData.service_type === 'Venta de minisplit' && equiposVenta.length > 0) {
        const eqIds = equiposVenta.map(eq => eq.inventory_id).filter(Boolean);
        if (eqIds.length > 0) {
          await axios.post(`${API}/inventory/assign-to-service`, {
            service_id: serviceId,
            equipment_ids: eqIds
          });
        }
      }

      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async (service) => {
    try {
      await axios.post(`${API}/services/${service.id}/duplicate`);
      toast.success('Servicio duplicado');
      fetchData();
    } catch (error) {
      toast.error('Error al duplicar');
    }
  };

  const handleDelete = async () => {
    if (!selectedService) return;
    try {
      await axios.delete(`${API}/services/${selectedService.id}`);
      toast.success('Servicio eliminado');
      setDeleteDialogOpen(false);
      setSelectedService(null);
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const openRescheduleDialog = (service) => {
    setServiceToReschedule(service);
    const tomorrow = shiftDateStr(todayMx(), 1);
    const costoActual = service.monto_total || service.quoted_cost || 0;
    
    setRescheduleData({
      nueva_fecha: tomorrow,
      hora_inicio: service.start_time || service.service_time || '09:00',
      hora_fin: service.end_time || '10:00',
      nuevo_tecnico_id: service.technician_id || '',
      nuevo_tecnico_nombre: service.technician_name || '',
      costo_total_cotizado: costoActual,
      anticipo: 0,
      motivo: '',
      estatus_evento_actual: 'reagendado',
      acudio_domicilio: false,
      observaciones_dia_actual: ''
    });
    setRescheduleDialogOpen(true);
  };

  const handleRescheduleService = async () => {
    if (!serviceToReschedule) return;
    
    if (!rescheduleData.nueva_fecha) {
      toast.error('Selecciona una nueva fecha');
      return;
    }

    const costoTotal = parseFloat(rescheduleData.costo_total_cotizado) || 0;
    const nuevoAnticipo = parseFloat(rescheduleData.anticipo) || 0;
    const anticipoAcumuladoAnterior = serviceToReschedule.anticipo_acumulado || serviceToReschedule.anticipo || 0;
    const nuevoAnticipoTotal = anticipoAcumuladoAnterior + nuevoAnticipo;
    
    if (costoTotal > 0 && nuevoAnticipoTotal > costoTotal) {
      toast.error(`El total de anticipos ($${nuevoAnticipoTotal.toLocaleString()}) no puede exceder el costo total ($${costoTotal.toLocaleString()})`);
      return;
    }

    setRescheduling(true);
    try {
      // Determinar tipo de movimiento
      const esCambioTecnico = rescheduleData.nuevo_tecnico_id !== serviceToReschedule.technician_id;
      const esAnticipacion = rescheduleData.nueva_fecha < serviceToReschedule.service_date;
      
      const response = await axios.post(`${API}/services/${serviceToReschedule.id}/movimiento`, {
        tipo_movimiento: esCambioTecnico ? 'reasignacion' : (esAnticipacion ? 'anticipado' : 'reagendado'),
        nueva_fecha: rescheduleData.nueva_fecha,
        hora_inicio: rescheduleData.hora_inicio,
        hora_fin: rescheduleData.hora_fin,
        nuevo_tecnico_id: rescheduleData.nuevo_tecnico_id || null,
        nuevo_tecnico_nombre: rescheduleData.nuevo_tecnico_nombre || '',
        estatus_evento_actual: rescheduleData.estatus_evento_actual,
        acudio_domicilio: rescheduleData.acudio_domicilio,
        observaciones_dia_actual: rescheduleData.observaciones_dia_actual,
        costo_total_cotizado: costoTotal > 0 ? costoTotal : null,
        anticipo: nuevoAnticipo,
        motivo: rescheduleData.motivo
      });
      
      const tipoMovimiento = response.data.resumen?.tipo || 'reagendado';
      const tipoTexto = tipoMovimiento === 'reasignacion' ? 'Reasignado' : 
                        tipoMovimiento === 'anticipado' ? 'Anticipado' : 'Reagendado';
      
      toast.success(`Servicio ${tipoTexto.toLowerCase()} exitosamente`);
      
      // Mostrar información del día original conservado
      if (response.data.evento_dia_original) {
        const diaOriginal = response.data.evento_dia_original;
        toast.info(`Registro del ${diaOriginal.fecha} conservado como "${diaOriginal.estatus}"`);
      }
      
      if (response.data.resumen) {
        const { resumen } = response.data;
        if (resumen.nuevo_anticipo > 0) {
          toast.success(`Anticipo registrado: $${resumen.nuevo_anticipo.toLocaleString()}`);
        }
        if (resumen.restante > 0) {
          toast.info(`Restante pendiente: $${resumen.restante.toLocaleString()}`);
        }
      }
      
      setRescheduleDialogOpen(false);
      setServiceToReschedule(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar');
    } finally {
      setRescheduling(false);
    }
  };

  const handleStatusChange = async (service, newStatus) => {
    // If changing to 'finalizado', open the finalize dialog
    if (newStatus === 'finalizado') {
      setServiceToFinalize(service);
      setFinalizeData({
        folio_nota: '',
        monto_cobrado: '',
        comentario_cierre: '',
        enviar_a_presupuesto: false
      });
      setFinalizeDialogOpen(true);
      return;
    }
    
    try {
      await axios.put(`${API}/services/${service.id}`, { status: newStatus });
      toast.success('Estado actualizado');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleFinalizeService = async () => {
    if (!serviceToFinalize) return;
    
    if (!finalizeData.folio_nota.trim()) {
      toast.error('El folio de la nota es requerido');
      return;
    }

    const montoCobrado = parseFloat(finalizeData.monto_cobrado) || 0;
    const tieneAnticipos = (serviceToFinalize.anticipo_acumulado || 0) > 0;
    
    // Si no tiene anticipos, el monto debe ser mayor a 0
    if (!tieneAnticipos && montoCobrado <= 0) {
      toast.error('El costo final es requerido y debe ser mayor a 0');
      return;
    }
    
    // Si tiene anticipos pero el monto es 0 y el restante es > 0, advertir
    if (tieneAnticipos && montoCobrado === 0 && (serviceToFinalize.restante_por_pagar || 0) > 0) {
      const confirmar = window.confirm(`Aún hay $${serviceToFinalize.restante_por_pagar.toLocaleString()} por cobrar. ¿Desea finalizar sin cobrar el restante?`);
      if (!confirmar) return;
    }

    setFinalizing(true);
    try {
      const response = await axios.post(`${API}/services/${serviceToFinalize.id}/finalize`, {
        folio_nota: finalizeData.folio_nota,
        monto_cobrado: montoCobrado,
        comentario_cierre: finalizeData.comentario_cierre,
        enviar_a_presupuesto: finalizeData.enviar_a_presupuesto
      });
      
      toast.success('Servicio finalizado exitosamente');
      
      if (response.data.presupuesto_creado) {
        toast.success(`Presupuesto creado: ${response.data.presupuesto_creado.folio}`);
      }
      
      setFinalizeDialogOpen(false);
      setServiceToFinalize(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al finalizar servicio');
    } finally {
      setFinalizing(false);
    }
  };

  // Handle reassign service
  const handleReassignService = async () => {
    if (!serviceToReassign) return;
    
    if (!reassignData.nueva_fecha) {
      toast.error('La nueva fecha es requerida');
      return;
    }
    
    if (!reassignData.motivo_reasignacion.trim()) {
      toast.error('El motivo de reasignación es requerido');
      return;
    }

    setReassigning(true);
    try {
      const response = await axios.post(`${API}/services/${serviceToReassign.id}/reassign`, {
        anticipo_cobrado: parseFloat(reassignData.anticipo_cobrado) || 0,
        comentario_visita: reassignData.comentario_visita,
        motivo_reasignacion: reassignData.motivo_reasignacion,
        nuevo_tecnico_id: reassignData.nuevo_tecnico_id || null,
        nuevo_tecnico_nombre: reassignData.nuevo_tecnico_nombre || '',
        nueva_fecha: reassignData.nueva_fecha,
        nueva_hora_inicio: reassignData.nueva_hora_inicio,
        nueva_hora_fin: reassignData.nueva_hora_fin,
        costo_total_servicio: parseFloat(reassignData.costo_total_servicio) || null
      });
      
      toast.success(response.data.message);
      setReassignDialogOpen(false);
      setServiceToReassign(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reasignar servicio');
    } finally {
      setReassigning(false);
    }
  };

  // Open reassign dialog
  const openReassignDialog = (service) => {
    setServiceToReassign(service);
    setReassignData({
      anticipo_cobrado: 0,
      comentario_visita: '',
      motivo_reasignacion: '',
      nuevo_tecnico_id: '',
      nuevo_tecnico_nombre: '',
      nueva_fecha: shiftDateStr(todayMx(), 1),
      nueva_hora_inicio: service.start_time || '09:00',
      nueva_hora_fin: service.end_time || '10:00',
      costo_total_servicio: service.monto_total || service.quoted_cost || 0
    });
    setReassignDialogOpen(true);
  };

  const handleSendToBudget = async (service) => {
    try {
      const response = await axios.post(`${API}/services/${service.id}/send-to-budget`);
      toast.success(`Presupuesto creado: ${response.data.budget.folio}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar a presupuestos');
    }
  };

  // Inline edit: save a single field
  const handleInlineSave = async (serviceId, field, value) => {
    try {
      await axios.put(`${API}/services/${serviceId}`, { [field]: value });
      toast.success('Actualizado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
      throw error; // Re-throw so InlineEdit can revert
    }
  };

  // Inline edit: save multiple fields at once
  const handleInlineSaveMulti = async (serviceId, updates) => {
    try {
      await axios.put(`${API}/services/${serviceId}`, updates);
      toast.success('Actualizado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
      throw error;
    }
  };

  // Inline edit: change technician (registers as a movement if different)
  const handleInlineTechnicianChange = async (service, newTechId) => {
    if (newTechId === service.technician_id) return;
    const newTech = technicians.find(t => t.id === newTechId);
    if (!newTech) return;
    try {
      // Use the movimiento endpoint to register the change properly
      await axios.post(`${API}/services/${service.id}/movimiento`, {
        nueva_fecha: service.service_date,
        hora_inicio: service.start_time || '09:00',
        hora_fin: service.end_time || '10:00',
        nuevo_tecnico_id: newTechId,
        nuevo_tecnico_nombre: newTech.name,
        costo_total_cotizado: service.monto_total || service.quoted_cost || 0,
        anticipo: 0,
        motivo: 'Cambio rápido de técnico desde tabla',
        estatus_evento_actual: 'pendiente',
        acudio_domicilio: false,
        observaciones_dia_actual: ''
      });
      toast.success(`Técnico cambiado a ${newTech.name}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar técnico');
      throw error;
    }
  };

  const filteredClients = clients.filter(c => 
    c.full_name.toLowerCase().includes(clientSearch.toLowerCase()) ||
    c.phones?.some(p => p.includes(clientSearch))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Contar servicios por técnico (dentro de los servicios filtrados)
  const getServiceCountByTechnician = (technicianName) => {
    if (technicianName === 'all') return displayServices.length;
    return displayServices.filter(s => s.technician_name === technicianName).length;
  };

  // Filtrar servicios por técnico seleccionado (tab)
  const servicesFilteredByTab = selectedTechnicianTab === 'all' 
    ? displayServices 
    : displayServices.filter(s => s.technician_name === selectedTechnicianTab);

  // Agrupar servicios por técnico
  const groupedServices = {};
  if (groupByTechnician) {
    servicesFilteredByTab.forEach(service => {
      const techName = service.technician_name || 'Sin asignar';
      if (!groupedServices[techName]) {
        groupedServices[techName] = [];
      }
      groupedServices[techName].push(service);
    });
  }

  // Lista de técnicos para las pestañas
  const technicianTabs = [
    { id: 'all', name: 'Todos' },
    ...technicians.filter(t => t.is_active !== false).map(t => ({ id: t.name, name: t.name }))
  ];

  // Compute available slots for selected technician
  const WORK_START = 8 * 60; // 8:00 AM in minutes
  const WORK_END = 20 * 60;  // 8:00 PM in minutes
  const COMMON_BLOCKS = [
    [8,0,9,0], [9,0,10,0], [10,0,11,0], [11,0,12,0],
    [12,0,13,0], [13,0,14,0], [14,0,15,0], [15,0,16,0],
    [16,0,17,0], [17,0,18,0], [18,0,19,0], [19,0,20,0]
  ];

  const toMin = (t) => { if (!t) return 0; const [h,m] = t.split(':').map(Number); return h*60+m; };
  const toTime = (m) => `${String(Math.floor(m/60)).padStart(2,'0')}:${String(m%60).padStart(2,'0')}`;

  const computeAvailability = () => {
    if (selectedTechnicianTab === 'all' || !filters.date_from || filters.date_from !== filters.date_to) return null;
    const today = todayMx();
    if (filters.date_from < today) return null;

    // Filter out cancelled/finalized services
    const activeServices = techSchedule
      .filter(s => !['cancelado','finalizado','visitado'].includes(s.status))
      .filter(s => s.start_time)
      .sort((a,b) => toMin(a.start_time) - toMin(b.start_time));

    // Calculate gaps
    const gaps = [];
    let cursor = WORK_START;
    for (const svc of activeServices) {
      const sStart = toMin(svc.start_time);
      const sEnd = toMin(svc.end_time || svc.start_time) || sStart + 60;
      if (sStart > cursor) {
        gaps.push({ start: cursor, end: sStart, duration: sStart - cursor });
      }
      cursor = Math.max(cursor, sEnd);
    }
    if (cursor < WORK_END) {
      gaps.push({ start: cursor, end: WORK_END, duration: WORK_END - cursor });
    }

    // Find practical suggested blocks that fit inside gaps
    const suggestions = [];
    for (const [sh,sm,eh,em] of COMMON_BLOCKS) {
      const bStart = sh*60+sm;
      const bEnd = eh*60+em;
      const fitsInGap = gaps.some(g => bStart >= g.start && bEnd <= g.end);
      if (fitsInGap) {
        suggestions.push({ start: bStart, end: bEnd });
      }
    }

    return { activeServices, gaps, suggestions, allFree: activeServices.length === 0 };
  };

  const availability = computeAvailability();

  // "Agendar aquí" - open new service with pre-filled tech + date + time
  const handleScheduleHere = (startMin, endMin) => {
    const tech = technicians.find(t => t.name === selectedTechnicianTab);
    setSelectedService(null);
    setFormData({
      ...getEmptyForm(),
      service_date: filters.date_from,
      start_time: toTime(startMin),
      end_time: toTime(endMin),
      technician_id: tech?.id || 'unassigned',
      technician_name: tech?.name || '',
      assigned_by: user.full_name,
      assigned_by_id: user.id
    });
    setEquiposVenta([]);
    axios.get(`${API}/inventory/available`).then(r => setAvailableEquipment(r.data)).catch(() => {});
    setDialogOpen(true);
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="services-page">
      {/* Header compacto */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Servicios</h2>
          <p className="text-muted-foreground text-sm">{servicesFilteredByTab.length} servicios</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant={groupByTechnician ? 'secondary' : 'outline'} 
            size="sm"
            onClick={() => setGroupByTechnician(!groupByTechnician)}
          >
            <Users className="h-4 w-4 mr-2" />
            {groupByTechnician ? 'Desagrupar' : 'Agrupar por Técnico'}
          </Button>
          <Button onClick={openNewDialog} data-testid="new-service-button">
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Servicio
          </Button>
        </div>
      </div>

      {/* Tarjetas resumen por técnico - Click para filtrar */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {technicianTabs.map(tech => {
          const count = getServiceCountByTechnician(tech.id);
          const colors = tech.id === 'all' 
            ? { bg: 'bg-slate-100 dark:bg-slate-800', text: 'text-slate-700 dark:text-slate-300', dot: 'bg-slate-500' }
            : getTechnicianColor(tech.name);
          const isSelected = selectedTechnicianTab === tech.id;
          
          return (
            <Card 
              key={tech.id}
              className={`cursor-pointer transition-all hover:shadow-md ${isSelected ? `ring-2 ring-primary ${colors.bg}` : 'hover:bg-muted/50'}`}
              onClick={() => setSelectedTechnicianTab(tech.id)}
              data-testid={`tech-card-${tech.id}`}
            >
              <CardContent className="p-3">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${colors.dot}`} />
                  <span className={`text-sm font-medium truncate ${isSelected ? colors.text : ''}`}>
                    {tech.id === 'all' ? 'Todos' : tech.name.split(' ')[0]}
                  </span>
                </div>
                <div className={`text-2xl font-bold mt-1 ${isSelected ? colors.text : ''}`}>
                  {count}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filtros principales - Siempre visibles */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Búsqueda */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por folio, cliente, teléfono, dirección..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="service-search-input"
              />
            </div>

            {/* Filtros rápidos de fecha */}
            <div className="flex gap-2 flex-wrap items-center">
              {(() => {
                const today = todayMxDate();
                const yesterdayStr = format(addDays(today, -1), 'yyyy-MM-dd');
                const todayFmt = format(today, 'yyyy-MM-dd');
                const tomorrowStr = format(addDays(today, 1), 'yyyy-MM-dd');
                const weekStart = format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd');
                const weekEnd = format(endOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd');
                const monthStart = format(startOfMonth(today), 'yyyy-MM-dd');
                const monthEnd = format(endOfMonth(today), 'yyyy-MM-dd');
                const noDateFilter = !filters.date_from && !filters.date_to;

                const dateButtons = [
                  { key: 'yesterday', label: 'Ayer', active: filters.date_from === yesterdayStr && filters.date_to === yesterdayStr },
                  { key: 'today', label: 'Hoy', active: filters.date_from === todayFmt && filters.date_to === todayFmt },
                  { key: 'tomorrow', label: 'Mañana', active: filters.date_from === tomorrowStr && filters.date_to === tomorrowStr },
                  { key: 'week', label: 'Semana', active: filters.date_from === weekStart && filters.date_to === weekEnd },
                  { key: 'month', label: 'Mes', active: filters.date_from === monthStart && filters.date_to === monthEnd },
                  { key: 'all_dates', label: 'Todos', active: noDateFilter },
                ];

                return dateButtons.map(btn => (
                  <Button
                    key={btn.key}
                    variant={btn.active ? 'secondary' : 'outline'}
                    size="sm"
                    onClick={() => handleQuickFilter(btn.key)}
                    data-testid={`filter-${btn.key}`}
                  >
                    {btn.label}
                  </Button>
                ));
              })()}

              {/* Estado */}
              <Select 
                value={filters.status} 
                onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger className="w-32 h-8">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {statusOptions.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Botón filtros avanzados */}
              <Button 
                variant={showAdvancedFilters ? 'secondary' : 'ghost'} 
                size="sm"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              >
                <Filter className="h-4 w-4 mr-1" />
                Más
              </Button>

              {/* Limpiar filtros */}
              {(filters.status !== 'all' || filters.date_from || selectedTechnicianTab !== 'all') && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => {
                    clearFilters();
                    setSelectedTechnicianTab('all');
                  }}
                >
                  <X className="h-4 w-4 mr-1" />
                  Limpiar
                </Button>
              )}
            </div>
          </div>

          {/* Filtros avanzados - Panel colapsable */}
          {showAdvancedFilters && (
            <div className="mt-4 pt-4 border-t grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground">Fecha desde</Label>
                <Input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
                  className="h-8"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Fecha hasta</Label>
                <Input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
                  className="h-8"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Asignó</Label>
                <Select 
                  value={filters.assigned_by_id} 
                  onValueChange={(value) => setFilters(prev => ({ ...prev, assigned_by_id: value }))}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.username}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Técnico</Label>
                <Select 
                  value={filters.technician_id} 
                  onValueChange={(value) => setFilters(prev => ({ ...prev, technician_id: value }))}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {technicians.filter(t => t.is_active !== false).map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pestañas de técnicos - Navegación rápida */}
      <div className="flex gap-1 overflow-x-auto pb-2 scrollbar-thin">
        {technicianTabs.map(tech => {
          const count = getServiceCountByTechnician(tech.id);
          const colors = tech.id === 'all' 
            ? { bg: 'bg-slate-500', text: 'text-white' }
            : getTechnicianColor(tech.name);
          const isSelected = selectedTechnicianTab === tech.id;
          
          return (
            <Button
              key={tech.id}
              variant={isSelected ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedTechnicianTab(tech.id)}
              className={`whitespace-nowrap ${isSelected ? '' : 'border-l-4 ' + colors.border}`}
            >
              <span className={`w-2 h-2 rounded-full mr-2 ${colors.dot}`} />
              {tech.id === 'all' ? 'Todos' : tech.name.split(' ')[0]}
              <Badge variant="secondary" className="ml-2 text-xs">
                {count}
              </Badge>
            </Button>
          );
        })}
      </div>

      {/* Espacios disponibles sugeridos */}
      {availability && !loadingSchedule && (
        <Card className="border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-950/10" data-testid="availability-panel">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-green-800 dark:text-green-300 flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Espacios disponibles — {selectedTechnicianTab} — {filters.date_from && format(parseISO(filters.date_from), 'dd/MM/yyyy')}
              </p>
              {availability.allFree && (
                <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border border-green-300">
                  Día libre
                </Badge>
              )}
            </div>

            {/* Occupied services */}
            {availability.activeServices.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <span className="text-xs text-muted-foreground self-center">Ocupado:</span>
                {availability.activeServices.map((svc, i) => (
                  <Badge key={i} variant="outline" className="text-xs bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800">
                    {svc.start_time}-{svc.end_time || '?'} · {svc.client_name}
                  </Badge>
                ))}
              </div>
            )}

            {/* Available gaps */}
            {availability.gaps.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <span className="text-xs text-muted-foreground self-center">Huecos:</span>
                {availability.gaps.map((g, i) => (
                  <Badge key={i} className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border border-green-300">
                    {toTime(g.start)} - {toTime(g.end)} ({Math.floor(g.duration/60)}h{g.duration%60 > 0 ? `${g.duration%60}m` : ''})
                  </Badge>
                ))}
              </div>
            )}

            {/* Practical suggested slots */}
          </CardContent>
        </Card>
      )}
      {loadingSchedule && selectedTechnicianTab !== 'all' && filters.date_from && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground p-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando disponibilidad...
        </div>
      )}

      {/* Services Table */}
      <Card>
        <CardContent className="p-0">
          {/* Modo agrupado por técnico */}
          {groupByTechnician ? (
            <div className="divide-y">
              {Object.entries(groupedServices).map(([techName, techServices]) => {
                const colors = getTechnicianColor(techName);
                return (
                  <div key={techName} className="p-4">
                    {/* Encabezado del grupo */}
                    <div className={`flex items-center gap-3 mb-3 p-3 rounded-lg ${colors.bg}`}>
                      <div className={`w-4 h-4 rounded-full ${colors.dot}`} />
                      <h3 className={`font-bold text-lg ${colors.text}`}>{techName}</h3>
                      <Badge variant="secondary">{techServices.length} servicios</Badge>
                    </div>
                    {/* Tabla de servicios del técnico */}
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[70px]">Folio</TableHead>
                            <TableHead className="w-[90px]">Fecha</TableHead>
                            <TableHead className="w-[80px]">Horario</TableHead>
                            <TableHead className="w-[150px]">Cliente</TableHead>
                            <TableHead className="w-[100px]">Teléfono</TableHead>
                            <TableHead className="w-[180px]">Domicilio</TableHead>
                            <TableHead className="w-[100px]">Estado</TableHead>
                            <TableHead className="w-[60px]">Acciones</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {techServices.map(service => (
                            <TableRow key={service.id}>
                              <TableCell className="font-mono text-xs font-bold">{service.folio}</TableCell>
                              <TableCell className="text-sm">
                                {service.service_date ? format(parseISO(service.service_date), 'dd/MM/yy') : '-'}
                              </TableCell>
                              <TableCell className="text-xs">
                                {service.start_time || ''} - {service.end_time || ''}
                              </TableCell>
                              <TableCell className="font-medium">{service.client_name}</TableCell>
                              <TableCell className="text-xs">
                                {service.phones?.join(', ') || '-'}
                              </TableCell>
                              <TableCell className="text-xs truncate max-w-[180px]">
                                {service.address || '-'}
                              </TableCell>
                              <TableCell>
                                <Badge className={`status-${service.status}`}>
                                  {statusLabels[service.status] || service.status}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-8 w-8">
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => openDetailDialog(service)}>
                                      <Eye className="h-4 w-4 mr-2" />Ver Detalle
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => openEditDialog(service)}>
                                      <Edit className="h-4 w-4 mr-2" />Editar
                                    </DropdownMenuItem>
                                    {service.status !== 'finalizado' && (
                                      <DropdownMenuItem 
                                        onClick={() => {
                                          setServiceToFinalize(service);
                                          setFinalizeDialogOpen(true);
                                        }}
                                        className="text-green-600"
                                      >
                                        <CheckCircle className="h-4 w-4 mr-2" />Finalizar
                                      </DropdownMenuItem>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                );
              })}
              {Object.keys(groupedServices).length === 0 && (
                <div className="p-8 text-center text-muted-foreground">
                  No se encontraron servicios
                </div>
              )}
            </div>
          ) : (
          /* Modo tabla normal */
          <div 
            id="services-table-container"
            className="max-h-[calc(100vh-450px)] min-h-[400px]"
            style={{
              overflowX: 'auto',
              overflowY: 'auto'
            }}
          >
            <div style={{minWidth: '1600px', width: 'max-content'}}>
              <Table>
                <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow>
                  <TableHead className="w-[70px]">Folio</TableHead>
                  <TableHead className="w-[90px]">Fecha</TableHead>
                  <TableHead className="w-[80px]">Horario</TableHead>
                  <TableHead className="w-[150px]">Cliente</TableHead>
                  <TableHead className="w-[100px]">Teléfonos</TableHead>
                  <TableHead className="w-[150px]">Domicilio</TableHead>
                  <TableHead className="w-[100px]">Servicio</TableHead>
                  <TableHead className="w-[130px]">Técnico</TableHead>
                  <TableHead className="w-[80px]">Costo</TableHead>
                  <TableHead className="w-[150px]">Descripción</TableHead>
                  <TableHead className="w-[100px]">Estado</TableHead>
                  <TableHead className="w-[60px] text-center">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {servicesFilteredByTab.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center py-8 text-muted-foreground">
                      No se encontraron servicios
                    </TableCell>
                  </TableRow>
                ) : (
                  servicesFilteredByTab.map((service) => {
                    const techColors = getTechnicianColor(service.technician_name);
                    const isFinalized = service.status === 'finalizado' || service.status === 'cancelado';
                    const isHistorical = service._es_evento_historico;
                    const readOnly = isFinalized || isHistorical;
                    return (
                    <TableRow 
                      key={service._display_id || service.id} 
                      data-testid={`service-row-${service._display_id || service.id}`}
                      className={`border-l-4 ${techColors.border} ${isFinalized ? 'opacity-75' : ''}`}
                    >
                      {/* Folio - solo lectura */}
                      <TableCell className="font-mono text-xs font-bold">
                        {service.folio}
                        {service.folio_nota && isFinalized && (
                          <span className="block text-blue-600 dark:text-blue-400 font-normal">N:{service.folio_nota}</span>
                        )}
                      </TableCell>
                      {/* Fecha - editable */}
                      <TableCell>
                        <InlineDate
                          value={service.service_date}
                          onSave={(v) => handleInlineSave(service.id, 'service_date', v)}
                          disabled={readOnly}
                        />
                      </TableCell>
                      {/* Horario - editable */}
                      <TableCell>
                        <InlineTime
                          startTime={service.start_time}
                          endTime={service.end_time}
                          onSave={(s, e) => handleInlineSaveMulti(service.id, { start_time: s, end_time: e })}
                          disabled={readOnly}
                        />
                      </TableCell>
                      {/* Cliente - editable */}
                      <TableCell>
                        <InlineText
                          value={service.client_name}
                          onSave={(v) => handleInlineSave(service.id, 'client_name', v)}
                          disabled={readOnly}
                          className="font-medium text-sm"
                        />
                      </TableCell>
                      {/* Teléfonos - editable */}
                      <TableCell>
                        <InlineText
                          value={service.phones?.join(', ')}
                          onSave={async (v) => {
                            const phones = v.split(',').map(p => p.trim()).filter(Boolean);
                            await handleInlineSave(service.id, 'phones', phones);
                          }}
                          disabled={readOnly}
                          className="text-xs"
                        />
                      </TableCell>
                      {/* Domicilio - editable */}
                      <TableCell>
                        <InlineText
                          value={service.address}
                          onSave={(v) => handleInlineSave(service.id, 'address', v)}
                          disabled={readOnly}
                          className="text-xs"
                        />
                      </TableCell>
                      {/* Servicio - dropdown editable */}
                      <TableCell>
                        <InlineSelect
                          value={service.service_type}
                          options={[
                            ...serviceTypes.map(t => ({ value: t, label: t })),
                            ...(service.service_type && !serviceTypes.includes(service.service_type) 
                              ? [{ value: service.service_type, label: service.service_type }] : [])
                          ]}
                          onSave={(v) => handleInlineSave(service.id, 'service_type', v)}
                          disabled={readOnly}
                          className="w-[100px]"
                        />
                      </TableCell>
                      {/* Técnico - dropdown editable (registra movimiento) */}
                      <TableCell>
                        {readOnly ? (
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${techColors.dot}`} />
                            <span className={`text-sm ${techColors.text}`}>
                              {service.technician_name || 'Sin asignar'}
                            </span>
                          </div>
                        ) : (
                          <InlineSelect
                            value={service.technician_id || 'unassigned'}
                            options={[
                              { value: 'unassigned', label: 'Sin asignar' },
                              ...technicians.filter(t => t.is_active !== false).map(t => ({ value: t.id, label: t.name }))
                            ]}
                            onSave={(v) => {
                              if (v === 'unassigned') {
                                return handleInlineSaveMulti(service.id, { technician_id: '', technician_name: '' });
                              }
                              return handleInlineTechnicianChange(service, v);
                            }}
                            renderValue={() => (
                              <div className="flex items-center gap-1">
                                <div className={`w-2 h-2 rounded-full ${techColors.dot}`} />
                                <span className={`text-xs ${techColors.text}`}>
                                  {service.technician_name || 'Sin asignar'}
                                </span>
                              </div>
                            )}
                            className="w-[120px]"
                          />
                        )}
                      </TableCell>
                      {/* Costo - editable con anticipo/restante */}
                      <TableCell className="text-sm">
                        {isFinalized && service.monto_cobrado ? (
                          <span className="text-green-600 dark:text-green-400 font-medium">
                            ${service.monto_cobrado?.toLocaleString()}
                          </span>
                        ) : readOnly ? (
                          <span className="text-muted-foreground">
                            ${(service.monto_total || service.quoted_cost || 0).toLocaleString()}
                          </span>
                        ) : (
                          <div className="space-y-0.5">
                            <InlineText
                              value={String(service.monto_total || service.quoted_cost || 0)}
                              onSave={(v) => handleInlineSaveMulti(service.id, { 
                                quoted_cost: parseFloat(v) || 0,
                                monto_total: parseFloat(v) || 0,
                                restante_por_pagar: (parseFloat(v) || 0) - (service.anticipo_acumulado || 0)
                              })}
                              type="number"
                              prefix="$"
                              className="text-sm"
                            />
                            {service.anticipo_acumulado > 0 && (
                              <p className="text-xs text-green-600 dark:text-green-400">
                                Ant: ${service.anticipo_acumulado?.toLocaleString()}
                              </p>
                            )}
                            {service.restante_por_pagar > 0 && (
                              <p className="text-xs text-orange-600 dark:text-orange-400">
                                Resta: ${service.restante_por_pagar?.toLocaleString()}
                              </p>
                            )}
                          </div>
                        )}
                      </TableCell>
                      {/* Descripción - editable */}
                      <TableCell>
                        <InlineText
                          value={service.description}
                          onSave={(v) => handleInlineSave(service.id, 'description', v)}
                          disabled={readOnly}
                          className="text-xs max-w-[150px] truncate block"
                        />
                      </TableCell>
                      {/* Estado - dropdown (finalizado abre modal) */}
                      <TableCell>
                        {isFinalized ? (
                          <Badge className={`status-${service.status}`}>
                            {statusLabels[service.status] || service.status}
                          </Badge>
                        ) : (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-auto p-0">
                              <Badge className={`status-${service.status} cursor-pointer`}>
                                {statusLabels[service.status] || service.status}
                              </Badge>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            {statusOptions.map(s => (
                              <DropdownMenuItem
                                key={s.value}
                                onClick={() => handleStatusChange(service, s.value)}
                              >
                                <Badge className={`status-${s.value} mr-2`}>●</Badge>
                                {s.label}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 hover:bg-muted"
                              data-testid={`service-actions-${service._display_id || service.id}`}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="z-50">
                            <DropdownMenuItem onClick={() => openDetailDialog(service)}>
                              <Eye className="h-4 w-4 mr-2" />
                              Ver Detalle
                            </DropdownMenuItem>
                            {!isFinalized && (
                              <DropdownMenuItem onClick={() => openEditDialog(service)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Editar Completo
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleDuplicate(service)}>
                              <Copy className="h-4 w-4 mr-2" />
                              Duplicar
                            </DropdownMenuItem>
                            {!isFinalized && service.status !== 'cancelado' && (
                              <DropdownMenuItem 
                                onClick={() => {
                                  setServiceToFinalize(service);
                                  setFinalizeData({
                                    folio_nota: '',
                                    monto_cobrado: '',
                                    comentario_cierre: '',
                                    enviar_a_presupuesto: false
                                  });
                                  setFinalizeDialogOpen(true);
                                }}
                                className="text-green-600"
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Finalizar Servicio
                              </DropdownMenuItem>
                            )}
                            {!isFinalized && service.status !== 'cancelado' && (
                              <DropdownMenuItem 
                                onClick={() => openRescheduleDialog(service)}
                                className="text-blue-600"
                              >
                                <CalendarClock className="h-4 w-4 mr-2" />
                                Reagendar
                              </DropdownMenuItem>
                            )}
                            {!isFinalized && service.status !== 'cancelado' && (
                              <DropdownMenuItem 
                                onClick={() => openReassignDialog(service)}
                                className="text-orange-600"
                              >
                                <UserPlus className="h-4 w-4 mr-2" />
                                Reasignar a Otro Técnico
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleSendToBudget(service)}>
                              <Send className="h-4 w-4 mr-2" />
                              Enviar a Presupuestos
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => {
                                setSelectedService(service);
                                setDeleteDialogOpen(true);
                              }}
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );})
                )}
              </TableBody>
              </Table>
            </div>
          </div>
          )}
        </CardContent>
      </Card>

      {/* Service Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedService ? 'Editar Servicio' : 'Nuevo Servicio'}
            </DialogTitle>
            <DialogDescription>
              {selectedService ? 'Modifica los datos del servicio' : 'Completa los datos para crear un nuevo servicio'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Client Search */}
            <div className="space-y-2">
              <Label>Buscar Cliente Existente</Label>
              <div className="relative">
                <Input
                  placeholder="Buscar por nombre o teléfono..."
                  value={clientSearch}
                  onChange={(e) => {
                    setClientSearch(e.target.value);
                    setShowClientSuggestions(e.target.value.length > 0);
                  }}
                  onFocus={() => setShowClientSuggestions(clientSearch.length > 0)}
                />
                {showClientSuggestions && filteredClients.length > 0 && (
                  <Card className="absolute z-10 w-full mt-1 max-h-60 overflow-auto">
                    {filteredClients.slice(0, 5).map(client => (
                      <div
                        key={client.id}
                        className="p-2 hover:bg-muted flex items-center justify-between gap-2 border-b last:border-b-0"
                      >
                        <div 
                          className="flex-1 cursor-pointer"
                          onClick={() => handleClientSelect(client)}
                        >
                          <p className="font-medium text-sm">{client.full_name}</p>
                          <p className="text-xs text-muted-foreground">{client.phones?.join(', ')}</p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={(e) => handleViewClientHistory(client, e)}
                          disabled={loadingHistory}
                        >
                          {loadingHistory ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <>
                              <FileText className="h-3 w-3 mr-1" />
                              Historial
                            </>
                          )}
                        </Button>
                      </div>
                    ))}
                  </Card>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fecha del Servicio *</Label>
                <Input
                  type="date"
                  value={formData.service_date}
                  onChange={(e) => setFormData({ ...formData, service_date: e.target.value })}
                  required
                  data-testid="service-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Hora de Inicio</Label>
                <Input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  data-testid="start-time-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Hora de Finalización</Label>
                <Input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  data-testid="end-time-input"
                />
              </div>
            </div>

            {/* Recomendación de horarios */}
            <ScheduleRecommendation
              technicianId={formData.technician_id}
              date={formData.service_date}
              startTime={formData.start_time}
              endTime={formData.end_time}
              excludeServiceId={selectedService?.id}
              onSelectSlot={(start, end) => setFormData({ ...formData, start_time: start, end_time: end })}
            />

            <div className="space-y-2">
              <Label>Nombre del Cliente *</Label>
              <Input
                value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                placeholder="Nombre completo"
                required
                data-testid="client-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Teléfonos</Label>
              {formData.phones.map((phone, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={phone}
                    onChange={(e) => handlePhoneChange(index, e.target.value)}
                    placeholder="Número de teléfono"
                    data-testid={`phone-input-${index}`}
                  />
                  {formData.phones.length > 1 && (
                    <Button type="button" variant="ghost" size="icon" onClick={() => removePhone(index)}>
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={addPhone}>
                <Plus className="h-4 w-4 mr-2" />
                Agregar Teléfono
              </Button>
            </div>

            <div className="space-y-2">
              <Label>Domicilio</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Dirección completa"
                data-testid="address-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Referencias del Domicilio</Label>
              <Input
                value={formData.address_reference}
                onChange={(e) => setFormData({ ...formData, address_reference: e.target.value })}
                placeholder="Ej: Casa azul, frente al parque"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo de Servicio *</Label>
                <Select 
                  value={formData.service_type} 
                  onValueChange={(v) => setFormData({ ...formData, service_type: v })}
                >
                  <SelectTrigger data-testid="service-type-select">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {serviceTypes.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Medio de Contacto</Label>
                <Select 
                  value={formData.contact_medium} 
                  onValueChange={(v) => setFormData({ ...formData, contact_medium: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {contactMediums.map(medium => (
                      <SelectItem key={medium} value={medium}>{medium}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Descripción del Trabajo</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe el problema o trabajo solicitado"
                rows={2}
                data-testid="description-textarea"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Técnico Asignado</Label>
                <Select 
                  value={formData.technician_id} 
                  onValueChange={handleTechnicianChange}
                >
                  <SelectTrigger data-testid="technician-select">
                    <SelectValue placeholder="Sin asignar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Sin asignar</SelectItem>
                    {technicians.filter(t => t.is_active).map(tech => (
                      <SelectItem key={tech.id} value={tech.id}>{tech.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Costo Cotizado</Label>
                <Input
                  type="number"
                  value={formData.quoted_cost}
                  onChange={(e) => setFormData({ ...formData, quoted_cost: e.target.value })}
                  placeholder="0"
                  min="0"
                  step="0.01"
                  data-testid="cost-input"
                />
              </div>
            </div>

            {/* Equipment section for Venta de minisplit */}
            {formData.service_type === 'Venta de minisplit' && (
              <div className="space-y-3 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800" data-testid="equipment-section">
                <p className="text-sm font-medium text-blue-800 dark:text-blue-300 flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Equipos de Inventario
                  {equiposVenta.length > 0 && <Badge variant="secondary" className="text-xs">{equiposVenta.length}</Badge>}
                </p>
                <div className="max-h-[320px] overflow-y-auto pr-1 space-y-3">
                {equiposVenta.map((eq, idx) => (
                  <div key={idx} className="p-3 bg-background rounded-lg border space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-blue-600">Equipo #{idx + 1}</span>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setEquiposVenta(equiposVenta.filter((_, i) => i !== idx))} className="h-6 w-6 p-0"><X className="h-3 w-3" /></Button>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Seleccionar equipo disponible</Label>
                      <Select
                        value={eq.inventory_id || ''}
                        onValueChange={(invId) => {
                          const item = availableEquipment.find(i => i.id === invId);
                          if (item) {
                            const updated = [...equiposVenta];
                            updated[idx] = { ...updated[idx], inventory_id: invId, modelo: item.modelo, capacidad: item.capacidad, serie_evaporador: item.serie_evaporador, serie_condensador: item.serie_condensador };
                            setEquiposVenta(updated);
                          }
                        }}
                      >
                        <SelectTrigger className="text-xs"><SelectValue placeholder="Seleccionar equipo..." /></SelectTrigger>
                        <SelectContent>
                          {availableEquipment
                            .filter(i => !equiposVenta.some(e => e.inventory_id === i.id && e.inventory_id !== eq.inventory_id))
                            .map(i => (
                              <SelectItem key={i.id} value={i.id}>
                                {i.numero_equipo || i.id.slice(0,6)} - {i.modelo} {i.capacidad}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {eq.inventory_id && (
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div><span className="text-muted-foreground">Marca:</span> <strong>{eq.modelo}</strong></div>
                        <div><span className="text-muted-foreground">Capacidad:</span> <strong>{eq.capacidad}</strong></div>
                        <div><span className="text-muted-foreground">Serie Evap:</span> <strong className="font-mono">{eq.serie_evaporador}</strong></div>
                        <div><span className="text-muted-foreground">Serie Cond:</span> <strong className="font-mono">{eq.serie_condensador}</strong></div>
                      </div>
                    )}
                  </div>
                ))}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setEquiposVenta([...equiposVenta, { inventory_id: '', modelo: '', capacidad: '', serie_evaporador: '', serie_condensador: '', precio_venta: 0, anticipo_equipo: 0, incluye_instalacion: false, observaciones_venta: '' }])}
                  data-testid="add-equipment-btn"
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />Agregar equipo
                </Button>
              </div>
            )}

            <div className="space-y-2">
              <Label>Estado</Label>
              <Select 
                value={formData.status} 
                onValueChange={(v) => setFormData({ ...formData, status: v })}
              >
                <SelectTrigger data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map(s => (
                    <SelectItem key={s.value} value={s.value}>
                      <div className="flex items-center gap-2">
                        <Badge className={`status-${s.value}`}>●</Badge>
                        {s.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-service-button">
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  selectedService ? 'Actualizar' : 'Crear Servicio'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Service Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Detalle del Servicio
            </DialogTitle>
          </DialogHeader>
          {selectedService && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Folio</p>
                  <p className="font-mono font-bold">{selectedService.folio}</p>
                </div>
                <Badge className={`status-${selectedService.status}`}>
                  {statusLabels[selectedService.status]}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Fecha</p>
                    <p className="text-sm font-medium">
                      {selectedService.service_date ? format(parseISO(selectedService.service_date), 'PPP', { locale: es }) : '-'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Horario</p>
                    <p className="text-sm font-medium">
                      {selectedService.start_time || selectedService.service_time || '-'}
                      {selectedService.end_time && ` - ${selectedService.end_time}`}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3 p-4 bg-muted/50 rounded-lg">
                <div className="flex items-start gap-2">
                  <User className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground">Cliente</p>
                    <p className="font-medium">{selectedService.client_name}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground">Teléfonos</p>
                    <p className="text-sm">{selectedService.phones?.join(', ') || '-'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground">Domicilio</p>
                    <p className="text-sm">{selectedService.address || '-'}</p>
                    {selectedService.address_reference && (
                      <p className="text-xs text-muted-foreground mt-1">Ref: {selectedService.address_reference}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Tipo de Servicio</p>
                  <p className="font-medium">{selectedService.service_type}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Técnico</p>
                  <p className="font-medium">{selectedService.technician_name || 'Sin asignar'}</p>
                </div>
              </div>

              {selectedService.description && (
                <div>
                  <p className="text-xs text-muted-foreground">Descripción</p>
                  <p className="text-sm mt-1">{selectedService.description}</p>
                </div>
              )}

              {/* Información de Costos y Pagos */}
              <div className="p-4 bg-muted/30 rounded-lg space-y-3">
                <p className="text-sm font-medium flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Información de Costos y Pagos
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Costo Total Cotizado</p>
                    <p className="text-lg font-bold">
                      ${(selectedService.monto_total || selectedService.quoted_cost || 0).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total Abonado</p>
                    <p className="text-lg font-bold text-green-600">
                      ${(selectedService.anticipo_acumulado || selectedService.anticipo || 0).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Restante</p>
                    <p className="text-lg font-bold text-orange-600">
                      ${(selectedService.restante_por_pagar || 0).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Técnico Asignado</p>
                    <p className="font-medium">{selectedService.technician_name || 'Sin asignar'}</p>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  <span>Fecha: {selectedService.service_date}</span>
                  <span className="mx-2">|</span>
                  <span>
                    Horario: {selectedService.start_time || selectedService.service_time || '-'}
                    {selectedService.end_time && ` - ${selectedService.end_time}`}
                  </span>
                </div>
              </div>

              {/* Historial de Asignaciones - NUEVO (Técnicos que han atendido) */}
              {selectedService.historial_asignaciones && selectedService.historial_asignaciones.length > 0 && (
                <div className="p-4 bg-purple-50 dark:bg-purple-950/30 rounded-lg border border-purple-200 dark:border-purple-800">
                  <p className="text-sm font-medium text-purple-800 dark:text-purple-400 mb-3 flex items-center gap-2">
                    <History className="h-4 w-4" />
                    Historial de Asignaciones ({selectedService.historial_asignaciones.length})
                  </p>
                  <p className="text-xs text-muted-foreground mb-3">
                    Todos los técnicos y movimientos de este servicio
                  </p>
                  <ScrollArea className="max-h-72">
                    <div className="space-y-3">
                      {selectedService.historial_asignaciones.map((asig, idx) => (
                        <div key={idx} className={`text-xs p-3 bg-background rounded-lg border space-y-2 ${
                          asig.tipo === 'reasignacion' ? 'border-l-4 border-l-purple-500' : 
                          asig.tipo === 'asignacion_inicial' ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-blue-500'
                        }`}>
                          <div className="flex justify-between items-start">
                            <div>
                              <span className={`font-semibold text-sm ${
                                asig.tipo === 'reasignacion' ? 'text-purple-600' : 
                                asig.tipo === 'asignacion_inicial' ? 'text-green-600' : 'text-blue-600'
                              }`}>
                                {asig.tipo === 'asignacion_inicial' ? '📋 Asignación Inicial' : 
                                 asig.tipo === 'reasignacion' ? '🔄 Reasignación' : '📅 Reagendado'} #{asig.numero}
                              </span>
                            </div>
                            <span className="text-muted-foreground">{asig.fecha_movimiento?.slice(0, 10)}</span>
                          </div>
                          
                          {asig.realizado_por && (
                            <p className="text-muted-foreground">Realizado por: {asig.realizado_por}</p>
                          )}
                          
                          {/* Técnico anterior (si hubo cambio) */}
                          {asig.tecnico_anterior_nombre && asig.tipo !== 'asignacion_inicial' && (
                            <div className="p-2 bg-red-50 dark:bg-red-950/30 rounded">
                              <p className="font-medium text-red-800 dark:text-red-400 flex items-center gap-1">
                                <span>Técnico anterior: {asig.tecnico_anterior_nombre}</span>
                              </p>
                              <p className="text-red-700 dark:text-red-500">
                                Estado: {asig.estado_tecnico_anterior_texto || asig.estado_tecnico_anterior}
                              </p>
                              {asig.comentarios_visita_anterior && (
                                <p className="text-muted-foreground italic mt-1">"{asig.comentarios_visita_anterior}"</p>
                              )}
                            </div>
                          )}
                          
                          {/* Datos de esta asignación */}
                          <div className="grid grid-cols-2 gap-2 pt-2 border-t">
                            <div>
                              <span className="text-muted-foreground block">Técnico:</span>
                              <span className="font-medium">{asig.tecnico_nombre || 'Sin asignar'}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground block">Estado:</span>
                              <span className={`font-medium ${
                                asig.estado_tecnico === 'pendiente' ? 'text-yellow-600' :
                                asig.estado_tecnico === 'visito' ? 'text-blue-600' :
                                asig.estado_tecnico === 'no_concluyo' ? 'text-orange-600' : 'text-purple-600'
                              }`}>
                                {asig.estado_tecnico === 'pendiente' ? 'Pendiente' :
                                 asig.estado_tecnico === 'visito' ? 'Visitó' :
                                 asig.estado_tecnico === 'no_concluyo' ? 'No concluyó' : 'Req. seguimiento'}
                              </span>
                            </div>
                            <div>
                              <span className="text-muted-foreground block">Fecha servicio:</span>
                              <span>{asig.fecha_servicio}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground block">Horario:</span>
                              <span>{asig.horario}</span>
                            </div>
                          </div>
                          
                          {/* Datos financieros */}
                          {(asig.costo_acordado > 0 || asig.anticipo_registrado > 0) && (
                            <div className="pt-2 border-t space-y-1">
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Costo acordado:</span>
                                <span>${(asig.costo_acordado || 0).toLocaleString()}</span>
                              </div>
                              {asig.anticipo_registrado > 0 && (
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Anticipo registrado:</span>
                                  <span className="text-green-600">${(asig.anticipo_registrado || 0).toLocaleString()}</span>
                                </div>
                              )}
                              <div className="flex justify-between font-medium">
                                <span className="text-muted-foreground">Acumulado:</span>
                                <span className="text-green-600">${(asig.anticipo_acumulado || 0).toLocaleString()}</span>
                              </div>
                              <div className="flex justify-between font-medium">
                                <span className="text-orange-600">Restante:</span>
                                <span className="text-orange-600">${(asig.restante || 0).toLocaleString()}</span>
                              </div>
                            </div>
                          )}
                          
                          {asig.motivo && (
                            <div className="pt-2 border-t">
                              <span className="text-muted-foreground block">Motivo:</span>
                              <span className="italic">{asig.motivo}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}

              {/* Historial legacy de Reagendados (para compatibilidad) */}
              {!selectedService.historial_asignaciones?.length && selectedService.historial_reagendados && selectedService.historial_reagendados.length > 0 && (
                <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-400 mb-3 flex items-center gap-2">
                    <History className="h-4 w-4" />
                    Historial de Reagendados ({selectedService.historial_reagendados.length})
                  </p>
                  <ScrollArea className="max-h-60">
                    <div className="space-y-3">
                      {selectedService.historial_reagendados.map((reg, idx) => (
                        <div key={idx} className="text-xs p-3 bg-background rounded-lg border space-y-2">
                          <div className="flex justify-between items-start">
                            <span className="font-semibold text-sm">Reagendado #{reg.numero}</span>
                            <span className="text-muted-foreground">{reg.fecha_reagendado?.slice(0, 10)}</span>
                          </div>
                          <p className="text-muted-foreground">Por: {reg.reagendado_por}</p>
                          <div className="grid grid-cols-2 gap-2 pt-2 border-t">
                            <div>
                              <span className="text-muted-foreground block">Fecha anterior:</span>
                              <span>{reg.fecha_anterior}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground block">Nueva fecha:</span>
                              <span className="font-medium">{reg.nueva_fecha}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground block">Técnico anterior:</span>
                              <span>{reg.tecnico_anterior_nombre || 'Sin asignar'}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground block">Técnico nuevo:</span>
                              <span className="font-medium">{reg.tecnico_nuevo_nombre || 'Sin asignar'}</span>
                            </div>
                          </div>
                          {reg.nuevo_anticipo > 0 && (
                            <p className="text-green-600">Anticipo: ${reg.nuevo_anticipo?.toLocaleString()}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}

              {/* Finalization Info */}
              {selectedService.status === 'finalizado' && (
                <div className="p-4 bg-green-50 dark:bg-green-950/30 rounded-lg space-y-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="font-medium text-green-800 dark:text-green-400">Servicio Finalizado</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Folio de la Nota</p>
                      <p className="font-mono font-medium">{selectedService.folio_nota || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Monto Cobrado</p>
                      <p className="text-lg font-bold text-green-600 dark:text-green-400">
                        ${selectedService.monto_cobrado?.toLocaleString() || 0}
                      </p>
                    </div>
                  </div>
                  {selectedService.comentario_cierre && (
                    <div>
                      <p className="text-xs text-muted-foreground">Comentario de Cierre</p>
                      <p className="text-sm">{selectedService.comentario_cierre}</p>
                    </div>
                  )}
                  {selectedService.fecha_cierre && (
                    <div className="text-xs text-muted-foreground">
                      Cerrado el {format(parseISO(selectedService.fecha_cierre), "dd/MM/yyyy 'a las' HH:mm", { locale: es })}
                      {selectedService.cerrado_por && ` por ${selectedService.cerrado_por}`}
                    </div>
                  )}
                </div>
              )}

              {selectedService.budget_info && (
                <div className="p-3 bg-cyan-50 dark:bg-cyan-950/30 rounded-lg">
                  <p className="text-xs font-medium text-cyan-800 dark:text-cyan-400">Información de Presupuesto</p>
                  <p className="text-sm mt-1">{selectedService.budget_info}</p>
                </div>
              )}

              {selectedService.process_info && (
                <div className="p-3 bg-purple-50 dark:bg-purple-950/30 rounded-lg">
                  <p className="text-xs font-medium text-purple-800 dark:text-purple-400">Información del Proceso</p>
                  <p className="text-sm mt-1">{selectedService.process_info}</p>
                </div>
              )}

              {selectedService.internal_comments && (
                <div className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
                  <p className="text-xs font-medium text-amber-800 dark:text-amber-400">Comentarios Internos</p>
                  <p className="text-sm mt-1">{selectedService.internal_comments}</p>
                </div>
              )}

              <div className="pt-4 border-t">
                <p className="text-xs text-muted-foreground mb-2">Registro</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Creado por</p>
                    <p>{selectedService.created_by}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Asignado por</p>
                    <p>{selectedService.assigned_by}</p>
                  </div>
                </div>
              </div>

              {selectedService.history?.length > 0 && (
                <div className="pt-4 border-t">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Historial de Cambios</p>
                  <ScrollArea className="h-40">
                    <div className="space-y-2">
                      {selectedService.history.map((log, idx) => (
                        <div key={idx} className="text-xs p-2 bg-muted/50 rounded">
                          <div className="flex justify-between">
                            <span className="font-medium">{log.action}</span>
                            <span className="text-muted-foreground">
                              {format(parseISO(log.timestamp), 'dd/MM/yy HH:mm')}
                            </span>
                          </div>
                          <p className="text-muted-foreground mt-1">Por: {log.performed_by}</p>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar Servicio</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de que deseas eliminar este servicio? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Finalize Service Dialog */}
      <Dialog open={finalizeDialogOpen} onOpenChange={setFinalizeDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Finalizar Servicio
            </DialogTitle>
            <DialogDescription>
              Completa los datos obligatorios para cerrar el servicio
            </DialogDescription>
          </DialogHeader>
          {serviceToFinalize && (
            <div className="space-y-4">
              <div className="p-4 bg-muted/50 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-mono text-sm font-medium">{serviceToFinalize.folio}</p>
                    <p className="text-sm font-medium mt-1">{serviceToFinalize.client_name}</p>
                    <p className="text-xs text-muted-foreground">{serviceToFinalize.service_type}</p>
                    {serviceToFinalize.visitas && serviceToFinalize.visitas.length > 0 && (
                      <Badge variant="secondary" className="mt-1">
                        <History className="h-3 w-3 mr-1" />
                        {serviceToFinalize.visitas.length} visita(s) anteriores
                      </Badge>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Costo total</p>
                    <p className="font-medium">${(serviceToFinalize.monto_total || serviceToFinalize.quoted_cost || 0).toLocaleString()}</p>
                    {serviceToFinalize.anticipo_acumulado > 0 && (
                      <>
                        <p className="text-xs text-green-600">Anticipos cobrados: ${serviceToFinalize.anticipo_acumulado.toLocaleString()}</p>
                        <p className="text-xs text-orange-600 font-medium">
                          Restante: ${(serviceToFinalize.restante_por_pagar || 0).toLocaleString()}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {serviceToFinalize.anticipo_acumulado > 0 && (
                <div className="p-3 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                  <p className="text-sm text-green-800 dark:text-green-400 font-medium mb-2">
                    Este servicio tiene anticipos
                  </p>
                  <div className="text-xs space-y-1 text-green-800 dark:text-green-400">
                    <p>Total anticipado: ${serviceToFinalize.anticipo_acumulado.toLocaleString()}</p>
                    <p className="font-medium">Solo cobra el restante: ${(serviceToFinalize.restante_por_pagar || 0).toLocaleString()}</p>
                  </div>
                </div>
              )}

              <div className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                <p className="text-sm text-amber-800 dark:text-amber-400 font-medium">
                  Los campos marcados con * son obligatorios
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="monto_cobrado" className="text-base font-semibold">
                  {serviceToFinalize.anticipo_acumulado > 0 ? 'Monto cobrado en esta visita *' : 'Costo Final *'}
                </Label>
                <Input
                  id="monto_cobrado"
                  type="number"
                  value={finalizeData.monto_cobrado}
                  onChange={(e) => setFinalizeData({ ...finalizeData, monto_cobrado: e.target.value })}
                  placeholder={serviceToFinalize.anticipo_acumulado > 0 ? `Sugerido: ${serviceToFinalize.restante_por_pagar || 0}` : "Ingresa el monto cobrado"}
                  min="0"
                  step="0.01"
                  className="text-lg"
                  data-testid="monto-cobrado-input"
                />
                <p className="text-xs text-muted-foreground">
                  {serviceToFinalize.anticipo_acumulado > 0 
                    ? `Total final será: $${((serviceToFinalize.anticipo_acumulado || 0) + parseFloat(finalizeData.monto_cobrado || 0)).toLocaleString()}`
                    : 'Monto real cobrado al cliente'}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="folio_nota" className="text-base font-semibold">Folio de la Nota *</Label>
                <Input
                  id="folio_nota"
                  value={finalizeData.folio_nota}
                  onChange={(e) => setFinalizeData({ ...finalizeData, folio_nota: e.target.value })}
                  placeholder="Ej: REP-2458"
                  className="text-lg font-mono"
                  data-testid="folio-nota-input"
                />
                <p className="text-xs text-muted-foreground">Número de factura o recibo</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="comentario_cierre">Comentario de Cierre</Label>
                <Textarea
                  id="comentario_cierre"
                  value={finalizeData.comentario_cierre}
                  onChange={(e) => setFinalizeData({ ...finalizeData, comentario_cierre: e.target.value })}
                  placeholder="Notas sobre el trabajo realizado"
                  rows={2}
                />
              </div>

              <div className="flex items-center space-x-2 p-3 bg-purple-50 dark:bg-purple-950/30 rounded-lg">
                <Checkbox
                  id="enviar_a_presupuesto"
                  checked={finalizeData.enviar_a_presupuesto}
                  onCheckedChange={(checked) => setFinalizeData({ ...finalizeData, enviar_a_presupuesto: checked })}
                  data-testid="enviar-presupuesto-checkbox"
                />
                <div className="grid gap-1.5 leading-none">
                  <label
                    htmlFor="enviar_a_presupuesto"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    Enviar a Presupuestos
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Crear un presupuesto con los datos de este servicio
                  </p>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setFinalizeDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleFinalizeService} 
                  disabled={finalizing}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="confirm-finalize-button"
                >
                  {finalizing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Finalizando...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Confirmar y Finalizar
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reschedule Service Dialog */}
      {/* Reschedule Service Dialog - Modal Completo */}
      <Dialog open={rescheduleDialogOpen} onOpenChange={setRescheduleDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-blue-600" />
              Reagendar Servicio
            </DialogTitle>
            <DialogDescription>
              Captura la información completa del nuevo acuerdo con el cliente
            </DialogDescription>
          </DialogHeader>
          {serviceToReschedule && (
            <div className="space-y-4">
              {/* Información del servicio actual */}
              <div className="p-4 bg-muted/50 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-mono text-sm font-medium">{serviceToReschedule.folio}</p>
                    <p className="text-sm font-medium mt-1">{serviceToReschedule.client_name}</p>
                    <p className="text-xs text-muted-foreground">{serviceToReschedule.service_type}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Fecha actual</p>
                    <p className="font-medium">{serviceToReschedule.service_date}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {serviceToReschedule.start_time || serviceToReschedule.service_time || '-'}
                      {serviceToReschedule.end_time && ` - ${serviceToReschedule.end_time}`}
                    </p>
                  </div>
                </div>
              </div>

              {/* Información de pago actual */}
              {(serviceToReschedule.anticipo_acumulado > 0 || serviceToReschedule.anticipo > 0 || serviceToReschedule.monto_total > 0) && (
                <div className="p-3 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                  <p className="text-xs font-medium text-green-800 dark:text-green-400 mb-2 flex items-center gap-2">
                    <Banknote className="h-4 w-4" />
                    Información de Pago Actual
                  </p>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <span className="text-xs text-muted-foreground block">Total</span>
                      <span className="font-medium">${(serviceToReschedule.monto_total || serviceToReschedule.quoted_cost || 0).toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground block">Anticipos</span>
                      <span className="font-medium text-green-600">${(serviceToReschedule.anticipo_acumulado || serviceToReschedule.anticipo || 0).toLocaleString()}</span>
                    </div>
                    <div>
                      <span className="text-xs text-muted-foreground block">Restante</span>
                      <span className="font-medium text-orange-600">${(serviceToReschedule.restante_por_pagar || 0).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Nueva fecha y rango de horario */}
              <div className="space-y-3 p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="text-sm font-medium text-blue-800 dark:text-blue-400 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Nueva Fecha y Horario
                </p>
                <div className="space-y-2">
                  <Label className="text-xs">Nueva Fecha *</Label>
                  <Input
                    type="date"
                    value={rescheduleData.nueva_fecha}
                    onChange={(e) => setRescheduleData({ ...rescheduleData, nueva_fecha: e.target.value })}
                    required
                    data-testid="reschedule-fecha-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs">Hora Desde</Label>
                    <Input
                      type="time"
                      value={rescheduleData.hora_inicio}
                      onChange={(e) => setRescheduleData({ ...rescheduleData, hora_inicio: e.target.value })}
                      data-testid="reschedule-hora-inicio-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Hora Hasta</Label>
                    <Input
                      type="time"
                      value={rescheduleData.hora_fin}
                      onChange={(e) => setRescheduleData({ ...rescheduleData, hora_fin: e.target.value })}
                      data-testid="reschedule-hora-fin-input"
                    />
                  </div>
                </div>
              </div>

              {/* Estado del día actual - SECCIÓN IMPORTANTE */}
              <div className="space-y-3 p-4 bg-purple-50 dark:bg-purple-950/30 rounded-lg border border-purple-200 dark:border-purple-800">
                <p className="text-sm font-medium text-purple-800 dark:text-purple-400 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  ¿Qué pasó hoy? ({serviceToReschedule.service_date})
                </p>
                <p className="text-xs text-muted-foreground">
                  Este registro se conservará en el día {serviceToReschedule.service_date}. 
                  <span className="font-medium"> El servicio NO desaparecerá de hoy.</span>
                </p>
                
                {/* ¿Acudió al domicilio? */}
                <div className="flex items-center space-x-3 p-2 bg-white dark:bg-zinc-900 rounded">
                  <input
                    type="checkbox"
                    id="acudio_domicilio"
                    checked={rescheduleData.acudio_domicilio}
                    onChange={(e) => setRescheduleData({ ...rescheduleData, acudio_domicilio: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <Label htmlFor="acudio_domicilio" className="text-sm cursor-pointer">
                    El técnico {serviceToReschedule.technician_name ? `(${serviceToReschedule.technician_name})` : ''} SÍ acudió al domicilio hoy
                  </Label>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-xs">Estatus de este día</Label>
                  <Select 
                    value={rescheduleData.estatus_evento_actual}
                    onValueChange={(v) => setRescheduleData({ ...rescheduleData, estatus_evento_actual: v })}
                  >
                    <SelectTrigger data-testid="reschedule-estatus-evento-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="reagendado">Reagendado</SelectItem>
                      <SelectItem value="visitado">Visitado (se atendió pero se moverá)</SelectItem>
                      <SelectItem value="anticipado">Anticipado (se adelanta la fecha)</SelectItem>
                      <SelectItem value="reasignado">Reasignado a otro técnico</SelectItem>
                      <SelectItem value="no_realizado">No realizado</SelectItem>
                      <SelectItem value="pendiente_segunda_visita">Pendiente segunda visita</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs">Observaciones del día de hoy</Label>
                  <Textarea
                    value={rescheduleData.observaciones_dia_actual}
                    onChange={(e) => setRescheduleData({ ...rescheduleData, observaciones_dia_actual: e.target.value })}
                    placeholder="Ej: Cliente no estaba, faltó material, técnico revisó y cotizó más trabajo..."
                    rows={2}
                    data-testid="reschedule-observaciones-dia-textarea"
                  />
                </div>
              </div>

              {/* Técnico asignado (Nuevo o mismo) */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  {serviceToReschedule.technician_name ? 'Asignar a Técnico (puede ser el mismo u otro)' : 'Asignar Técnico'}
                </Label>
                <Select 
                  value={rescheduleData.nuevo_tecnico_id || 'unassigned'} 
                  onValueChange={(v) => {
                    if (v === 'unassigned') {
                      setRescheduleData({ ...rescheduleData, nuevo_tecnico_id: '', nuevo_tecnico_nombre: '' });
                    } else {
                      const tech = technicians.find(t => t.id === v);
                      setRescheduleData({ 
                        ...rescheduleData, 
                        nuevo_tecnico_id: v, 
                        nuevo_tecnico_nombre: tech?.name || '' 
                      });
                    }
                  }}
                >
                  <SelectTrigger data-testid="reschedule-tecnico-select">
                    <SelectValue placeholder="Seleccionar técnico" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Sin asignar</SelectItem>
                    {technicians.filter(t => t.is_active).map(tech => (
                      <SelectItem key={tech.id} value={tech.id}>
                        {tech.name}
                        {tech.id === serviceToReschedule.technician_id && ' (actual)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {rescheduleData.nuevo_tecnico_id && rescheduleData.nuevo_tecnico_id !== serviceToReschedule.technician_id && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1">
                    <span className="font-medium">Nota:</span> Esto se registrará como una REASIGNACIÓN de técnico
                  </p>
                )}
              </div>

              {/* Sección de pagos */}
              <div className="space-y-3 p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-400 flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Acuerdo de Pago
                </p>
                
                <div className="space-y-2">
                  <Label className="text-xs">Costo Total Cotizado</Label>
                  <Input
                    type="number"
                    value={rescheduleData.costo_total_cotizado}
                    onChange={(e) => setRescheduleData({ ...rescheduleData, costo_total_cotizado: e.target.value })}
                    placeholder="0"
                    min="0"
                    step="0.01"
                    data-testid="reschedule-costo-total-input"
                  />
                  <p className="text-xs text-muted-foreground">El monto total acordado con el cliente para este servicio</p>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs">Anticipo (nuevo)</Label>
                  <Input
                    type="number"
                    value={rescheduleData.anticipo}
                    onChange={(e) => setRescheduleData({ ...rescheduleData, anticipo: e.target.value })}
                    placeholder="0"
                    min="0"
                    step="0.01"
                    data-testid="reschedule-anticipo-input"
                  />
                  <p className="text-xs text-muted-foreground">Cuánto dio el cliente en este reagendado</p>
                </div>

                {/* Cálculo automático del restante */}
                {(parseFloat(rescheduleData.costo_total_cotizado) > 0 || parseFloat(rescheduleData.anticipo) > 0) && (
                  <div className="mt-3 p-3 bg-white dark:bg-zinc-900 rounded-lg border">
                    <p className="text-xs font-medium text-muted-foreground mb-2">Resumen del Acuerdo:</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Costo total cotizado:</span>
                        <span className="font-medium">${parseFloat(rescheduleData.costo_total_cotizado || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Anticipos anteriores:</span>
                        <span className="text-green-600">${(serviceToReschedule.anticipo_acumulado || serviceToReschedule.anticipo || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Nuevo anticipo:</span>
                        <span className="text-green-600">+ ${parseFloat(rescheduleData.anticipo || 0).toLocaleString()}</span>
                      </div>
                      <div className="border-t pt-1 flex justify-between font-medium">
                        <span className="text-muted-foreground">Total anticipado:</span>
                        <span className="text-green-600">
                          ${((serviceToReschedule.anticipo_acumulado || serviceToReschedule.anticipo || 0) + parseFloat(rescheduleData.anticipo || 0)).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between font-semibold text-base">
                        <span className="text-orange-600">Restante pendiente:</span>
                        <span className="text-orange-600">
                          ${Math.max(0, (parseFloat(rescheduleData.costo_total_cotizado || 0) - ((serviceToReschedule.anticipo_acumulado || serviceToReschedule.anticipo || 0) + parseFloat(rescheduleData.anticipo || 0)))).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Motivo del movimiento */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Motivo del Cambio
                </Label>
                <Textarea
                  value={rescheduleData.motivo}
                  onChange={(e) => setRescheduleData({ ...rescheduleData, motivo: e.target.value })}
                  placeholder="Ej: Se le cotizó en $4,200, anticipo de $700. Cliente solicitó cambio de fecha por viaje..."
                  rows={3}
                  data-testid="reschedule-motivo-textarea"
                />
                <p className="text-xs text-muted-foreground">Explica por qué se movió y qué se acordó con el cliente</p>
              </div>

              {/* Historial de asignaciones (NUEVO) */}
              {serviceToReschedule.historial_asignaciones && serviceToReschedule.historial_asignaciones.length > 0 && (
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-2">
                    <History className="h-4 w-4" />
                    Historial de Asignaciones ({serviceToReschedule.historial_asignaciones.length})
                  </p>
                  <ScrollArea className="h-32">
                    <div className="space-y-2">
                      {serviceToReschedule.historial_asignaciones.map((asig, idx) => (
                        <div key={idx} className="text-xs p-2 bg-background rounded border">
                          <div className="flex justify-between items-start">
                            <div>
                              <span className={`font-medium ${asig.tipo === 'reasignacion' ? 'text-purple-600' : 'text-blue-600'}`}>
                                {asig.tipo === 'asignacion_inicial' ? 'Asignación Inicial' : 
                                 asig.tipo === 'reasignacion' ? 'Reasignación' : 'Reagendado'} #{asig.numero}
                              </span>
                              <span className="text-muted-foreground ml-2">{asig.fecha_movimiento?.slice(0, 10)}</span>
                            </div>
                          </div>
                          <div className="mt-1 grid grid-cols-2 gap-2">
                            <div>
                              <span className="text-muted-foreground">Técnico: </span>
                              <span className="font-medium">{asig.tecnico_nombre || 'Sin asignar'}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Fecha: </span>
                              <span>{asig.fecha_servicio}</span>
                            </div>
                          </div>
                          {asig.estado_tecnico_anterior_texto && asig.tipo !== 'asignacion_inicial' && (
                            <p className="text-purple-600 mt-1">
                              Técnico anterior ({asig.tecnico_anterior_nombre}): {asig.estado_tecnico_anterior_texto}
                            </p>
                          )}
                          {asig.anticipo_registrado > 0 && (
                            <p className="text-green-600">Anticipo: ${asig.anticipo_registrado?.toLocaleString()}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}

              {/* Historial legacy de reagendados (para compatibilidad) */}
              {!serviceToReschedule.historial_asignaciones?.length && serviceToReschedule.historial_reagendados && serviceToReschedule.historial_reagendados.length > 0 && (
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-2">
                    <History className="h-4 w-4" />
                    Reagendados Anteriores ({serviceToReschedule.historial_reagendados.length})
                  </p>
                  <ScrollArea className="h-24">
                    <div className="space-y-2">
                      {serviceToReschedule.historial_reagendados.map((reg, idx) => (
                        <div key={idx} className="text-xs p-2 bg-background rounded border">
                          <div className="flex justify-between">
                            <span className="font-medium">Reagendado #{reg.numero}</span>
                            <span className="text-muted-foreground">{reg.fecha_reagendado?.slice(0, 10)}</span>
                          </div>
                          <p className="text-muted-foreground">{reg.fecha_anterior} → {reg.nueva_fecha}</p>
                          {reg.nuevo_anticipo > 0 && (
                            <p className="text-green-600">Anticipo: ${reg.nuevo_anticipo?.toLocaleString()}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}

              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setRescheduleDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleRescheduleService} 
                  disabled={rescheduling || !rescheduleData.nueva_fecha}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="reschedule-confirm-button"
                >
                  {rescheduling ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Reagendando...
                    </>
                  ) : (
                    <>
                      <CalendarClock className="h-4 w-4 mr-2" />
                      Confirmar Reagendado
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Client History Dialog */}
      <Dialog open={clientHistoryOpen} onOpenChange={setClientHistoryOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Historial de {clientHistory?.full_name || 'Cliente'}
            </DialogTitle>
          </DialogHeader>
          
          {clientHistory && (
            <div className="flex-1 overflow-auto space-y-4">
              {/* Info del cliente */}
              <div className="grid grid-cols-2 gap-3 p-3 bg-muted/50 rounded-lg">
                <div>
                  <p className="text-xs text-muted-foreground">Teléfonos</p>
                  <p className="text-sm font-medium">{clientHistory.phones?.join(', ') || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Último servicio</p>
                  <p className="text-sm font-medium">
                    {clientHistory.last_service_date ? format(parseISO(clientHistory.last_service_date), 'dd/MM/yyyy') : 'Ninguno'}
                  </p>
                </div>
              </div>

              {/* Estadísticas rápidas */}
              <div className="grid grid-cols-3 gap-2">
                <div className="p-2 bg-blue-50 dark:bg-blue-950/30 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-600">{clientHistory.historial?.filter(h => h.tipo_movimiento === 'Servicio').length || 0}</p>
                  <p className="text-xs text-muted-foreground">Servicios</p>
                </div>
                <div className="p-2 bg-purple-50 dark:bg-purple-950/30 rounded-lg text-center">
                  <p className="text-2xl font-bold text-purple-600">{clientHistory.historial?.filter(h => h.tipo_movimiento === 'Presupuesto').length || 0}</p>
                  <p className="text-xs text-muted-foreground">Presupuestos</p>
                </div>
                <div className="p-2 bg-green-50 dark:bg-green-950/30 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{clientHistory.tecnicos_que_atendieron?.length || 0}</p>
                  <p className="text-xs text-muted-foreground">Técnicos</p>
                </div>
              </div>

              {/* Historial */}
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Historial ({clientHistory.historial?.length || 0} registros)
                </h4>
                <ScrollArea className="h-64">
                  {clientHistory.historial?.length > 0 ? (
                    <div className="space-y-2 pr-2">
                      {clientHistory.historial.map((item, idx) => (
                        <div key={idx} className="p-3 border rounded-lg">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant={item.tipo_movimiento === 'Servicio' ? 'default' : 'secondary'} className="text-xs">
                                  {item.tipo_movimiento}
                                </Badge>
                                <span className="font-mono text-xs text-muted-foreground">{item.folio}</span>
                                <Badge className={`status-${item.estado} text-xs`}>
                                  {statusLabels[item.estado] || item.estado}
                                </Badge>
                              </div>
                              <p className="font-medium text-sm">{item.servicio_tipo}</p>
                              {item.descripcion && (
                                <p className="text-xs text-muted-foreground mt-1">{item.descripcion}</p>
                              )}
                              {item.comentarios && (
                                <p className="text-xs text-muted-foreground mt-1 italic">"{item.comentarios}"</p>
                              )}
                            </div>
                            <div className="text-right text-xs">
                              <p className="font-medium">{item.fecha ? format(parseISO(item.fecha), 'dd/MM/yy') : '-'}</p>
                              <p className="text-muted-foreground">{item.tecnico_nombre || 'Sin asignar'}</p>
                              {item.monto_cobrado > 0 && (
                                <p className="text-green-600 font-medium">${item.monto_cobrado.toLocaleString()}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">Sin historial</p>
                  )}
                </ScrollArea>
              </div>
            </div>
          )}

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setClientHistoryOpen(false)}>
              Cerrar
            </Button>
            <Button onClick={selectClientFromHistory}>
              <User className="h-4 w-4 mr-2" />
              Seleccionar este Cliente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign Service Dialog */}
      <Dialog open={reassignDialogOpen} onOpenChange={setReassignDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-orange-600" />
              Reasignar Servicio
            </DialogTitle>
            <DialogDescription>
              Registra la visita actual y reasigna a otro técnico
            </DialogDescription>
          </DialogHeader>
          {serviceToReassign && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              {/* Info del servicio */}
              <div className="p-4 bg-muted/50 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-mono text-sm font-medium">{serviceToReassign.folio}</p>
                    <p className="text-sm font-medium mt-1">{serviceToReassign.client_name}</p>
                    <p className="text-xs text-muted-foreground">{serviceToReassign.service_type}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Técnico actual: {serviceToReassign.technician_name || 'Sin asignar'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Costo estimado</p>
                    <p className="font-medium">${(serviceToReassign.monto_total || serviceToReassign.quoted_cost || 0).toLocaleString()}</p>
                    {serviceToReassign.anticipo_acumulado > 0 && (
                      <p className="text-xs text-green-600">
                        Anticipos: ${serviceToReassign.anticipo_acumulado.toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
                {serviceToReassign.visitas && serviceToReassign.visitas.length > 0 && (
                  <div className="mt-2 pt-2 border-t">
                    <Badge variant="secondary">
                      <History className="h-3 w-3 mr-1" />
                      {serviceToReassign.visitas.length} visita(s) anteriores
                    </Badge>
                  </div>
                )}
              </div>

              {/* Datos de la visita actual */}
              <div className="p-3 bg-orange-50 dark:bg-orange-950/30 rounded-lg border border-orange-200 dark:border-orange-800">
                <p className="text-sm font-semibold text-orange-800 dark:text-orange-400 mb-3">
                  Datos de esta visita
                </p>
                
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-xs">Anticipo cobrado en esta visita</Label>
                    <Input
                      type="number"
                      value={reassignData.anticipo_cobrado}
                      onChange={(e) => setReassignData({ ...reassignData, anticipo_cobrado: e.target.value })}
                      placeholder="0"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs">Comentario de lo realizado</Label>
                    <Textarea
                      value={reassignData.comentario_visita}
                      onChange={(e) => setReassignData({ ...reassignData, comentario_visita: e.target.value })}
                      placeholder="Describe lo que se realizó en esta visita..."
                      rows={2}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-xs">Motivo de reasignación *</Label>
                    <Textarea
                      value={reassignData.motivo_reasignacion}
                      onChange={(e) => setReassignData({ ...reassignData, motivo_reasignacion: e.target.value })}
                      placeholder="¿Por qué no se puede terminar? Ej: Falta material, requiere especialista..."
                      rows={2}
                    />
                  </div>
                </div>
              </div>

              {/* Reasignación */}
              <div className="p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="text-sm font-semibold text-blue-800 dark:text-blue-400 mb-3">
                  Nueva asignación
                </p>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs">Nueva fecha *</Label>
                    <Input
                      type="date"
                      value={reassignData.nueva_fecha}
                      onChange={(e) => setReassignData({ ...reassignData, nueva_fecha: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Nuevo técnico</Label>
                    <Select 
                      value={reassignData.nuevo_tecnico_id} 
                      onValueChange={(v) => {
                        const tech = technicians.find(t => t.id === v);
                        setReassignData({ 
                          ...reassignData, 
                          nuevo_tecnico_id: v,
                          nuevo_tecnico_nombre: tech?.name || ''
                        });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent>
                        {technicians.filter(t => t.is_active).map(tech => (
                          <SelectItem key={tech.id} value={tech.id}>{tech.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Hora inicio</Label>
                    <Input
                      type="time"
                      value={reassignData.nueva_hora_inicio}
                      onChange={(e) => setReassignData({ ...reassignData, nueva_hora_inicio: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Hora fin</Label>
                    <Input
                      type="time"
                      value={reassignData.nueva_hora_fin}
                      onChange={(e) => setReassignData({ ...reassignData, nueva_hora_fin: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2 mt-3">
                  <Label className="text-xs">Actualizar costo total (opcional)</Label>
                  <Input
                    type="number"
                    value={reassignData.costo_total_servicio}
                    onChange={(e) => setReassignData({ ...reassignData, costo_total_servicio: e.target.value })}
                    placeholder="Dejar vacío para mantener el costo actual"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              {/* Resumen de pagos */}
              {(parseFloat(reassignData.anticipo_cobrado) > 0 || serviceToReassign.anticipo_acumulado > 0) && (
                <div className="p-3 bg-green-50 dark:bg-green-950/30 rounded-lg">
                  <p className="text-sm font-semibold text-green-800 dark:text-green-400 mb-2">
                    Resumen de pagos
                  </p>
                  <div className="text-xs space-y-1 text-green-800 dark:text-green-400">
                    <p>Anticipos anteriores: ${(serviceToReassign.anticipo_acumulado || 0).toLocaleString()}</p>
                    <p>+ Anticipo esta visita: ${parseFloat(reassignData.anticipo_cobrado || 0).toLocaleString()}</p>
                    <p className="font-medium border-t pt-1">
                      Total anticipos: ${((serviceToReassign.anticipo_acumulado || 0) + parseFloat(reassignData.anticipo_cobrado || 0)).toLocaleString()}
                    </p>
                    <p className="text-orange-600 dark:text-orange-400 font-medium">
                      Restante por cobrar: ${Math.max(0, (parseFloat(reassignData.costo_total_servicio) || serviceToReassign.monto_total || serviceToReassign.quoted_cost || 0) - ((serviceToReassign.anticipo_acumulado || 0) + parseFloat(reassignData.anticipo_cobrado || 0))).toLocaleString()}
                    </p>
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setReassignDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleReassignService} 
                  disabled={reassigning}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  {reassigning ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Reasignando...
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Confirmar Reasignación
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Service Visit History Dialog */}
      <Dialog open={serviceDetailOpen} onOpenChange={setServiceDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Historial de Visitas
            </DialogTitle>
          </DialogHeader>
          {selectedServiceDetail && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="flex justify-between">
                  <div>
                    <p className="font-mono text-sm font-medium">{selectedServiceDetail.folio}</p>
                    <p className="text-sm">{selectedServiceDetail.client_name}</p>
                    <p className="text-xs text-muted-foreground">{selectedServiceDetail.service_type}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Total servicio</p>
                    <p className="font-medium">${(selectedServiceDetail.monto_total || selectedServiceDetail.quoted_cost || 0).toLocaleString()}</p>
                    {selectedServiceDetail.anticipo_acumulado > 0 && (
                      <>
                        <p className="text-xs text-green-600">Anticipos: ${selectedServiceDetail.anticipo_acumulado.toLocaleString()}</p>
                        <p className="text-xs text-orange-600 font-medium">
                          Restante: ${(selectedServiceDetail.restante_por_pagar || 0).toLocaleString()}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-medium text-sm">Visitas registradas ({selectedServiceDetail.visitas?.length || 0})</h4>
                {selectedServiceDetail.visitas?.map((visita, index) => (
                  <div key={index} className="p-3 border rounded-lg">
                    <div className="flex justify-between items-start mb-2">
                      <Badge variant={visita.es_visita_final ? "default" : "secondary"}>
                        Visita #{visita.numero_visita} {visita.es_visita_final && '(Final)'}
                      </Badge>
                      <span className="text-xs text-muted-foreground">{visita.fecha}</span>
                    </div>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-muted-foreground">Técnico:</span> {visita.tecnico_nombre || 'Sin asignar'}</p>
                      <p><span className="text-muted-foreground">Horario:</span> {visita.hora_inicio}{visita.hora_fin && ` - ${visita.hora_fin}`}</p>
                      {visita.anticipo_cobrado > 0 && (
                        <p className="text-green-600 font-medium">
                          <span className="text-muted-foreground">Anticipo:</span> ${visita.anticipo_cobrado.toLocaleString()}
                        </p>
                      )}
                      {visita.monto_cobrado > 0 && (
                        <p className="text-green-600 font-medium">
                          <span className="text-muted-foreground">Monto cobrado:</span> ${visita.monto_cobrado.toLocaleString()}
                        </p>
                      )}
                      {visita.comentario && (
                        <p className="text-xs text-muted-foreground mt-2 p-2 bg-muted/50 rounded">
                          {visita.comentario}
                        </p>
                      )}
                      {visita.motivo_reasignacion && (
                        <p className="text-xs text-orange-600 mt-1">
                          <AlertCircle className="h-3 w-3 inline mr-1" />
                          Motivo: {visita.motivo_reasignacion}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Registrado por: {visita.registrado_por}
                      </p>
                    </div>
                  </div>
                ))}
                {(!selectedServiceDetail.visitas || selectedServiceDetail.visitas.length === 0) && (
                  <p className="text-center text-muted-foreground py-4">
                    No hay visitas registradas aún
                  </p>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setServiceDetailOpen(false)}>
                  Cerrar
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
